import { IF, nil, NO, UILink, UIRoute, UITextView, UIView, UIViewController, YES } from "uicore-ts"
import { CompanyViewController } from "./CompanyView/CompanyViewController"
import { RHFocusManager } from "./Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHPageFocusManager } from "./Custom components/RH/Keyboard navigation/RHPageFocusManager"
import { RHButton } from "./Custom components/RH/RHButton"
import { RHCalloutView } from "./Custom components/RH/RHCalloutView"
import {
    RHButtons,
    RHColors,
    RHFont,
    RHRadius,
    RHShadow,
    RHSpacing,
    RHTransition
} from "./Custom components/RH/Utils_slop/RHDesignTokens"
import { LogViewController } from "./EventLog/LogViewController"
import { FeedbackViewController } from "./FeedbackViewController"
import { ProcurementSearchViewController } from "./Search/ProcurementSearchViewController"
import { StatisticsMainViewController } from "./StatisticsMainViewController"
import { UserMainViewController } from "./UserMain/UserMainViewController"


// ============================================================================
// Building blocks
// ============================================================================


type HelpFlowIconName =
    | "search"
    | "visibility"
    | "register"
    | "edit"
    | "send"
    | "message"
    | "deadline"
    | "open"
    | "evaluate"
    | "winner"
    | "contract"
    | "document"
    | "team"
    | "preview"


type HelpFlowStep = {
    icon: HelpFlowIconName
    title: string
    detail: string
}


type HelpApplicationMode = "procurer" | "offerer"


type HelpTableOfContentsEntry = {
    text: string
    sectionIdentifier: string
    card: HelpCardView
}


// A vertical stack of child views. Every row supplies its own intrinsic height,
// so the page can use native scrolling without an inner scroll view.
class HelpStackView extends UIView {

    readonly _rows: { view: UIView; topGap: number }[] = []
    _innerInset = 0

    addRow(view: UIView, topGap: number): this {
        view.addedAsSubviewToView(this)
        this._rows.push({ view: view, topGap: topGap })
        return this
    }

    override layoutSubviews() {
        super.layoutSubviews()
        const bounds = this.contentBoundsWithInset(this._innerInset)
        let previousFrame = bounds.rectangleWithHeight(0)
        for (const row of this._rows) {
            previousFrame = previousFrame
                .rectangleForNextRow(row.topGap, row.view)
                .assignedAsFrameOfView(row.view)
        }
    }

    override intrinsicContentHeight(constrainingWidth: number = this.bounds.width): number {
        const innerWidth = constrainingWidth - this._innerInset * 2
        let totalHeight = this._innerInset * 2
        for (const row of this._rows) {
            totalHeight = totalHeight + row.topGap + row.view.intrinsicContentHeight(innerWidth)
        }
        return totalHeight
    }

}


// A titled white surface. Existing Help-page visual language is intentionally
// preserved while the content hierarchy inside the cards is strengthened.
class HelpCardView extends HelpStackView {

    readonly _headingLabel: UITextView

    constructor(titleText: string) {
        super()
        this._innerInset = RHSpacing.large
        ;(this as HelpCardView).configureWithObject({
            backgroundColor: RHColors.white,
            style: {
                borderRadius: `${RHRadius.large}px`,
                border: `1px solid ${RHColors.borderLight.stringValue}`,
                boxShadow: RHShadow.subtle,
                scrollMarginTop: `${RHSpacing.large}px`
            }
        })
        this._headingLabel = new UITextView(undefined, UITextView.type.header3).configuredWithObject({
            text: titleText,
            textColor: RHColors.textHeading,
            isSingleLine: NO,
            style: {
                fontFamily: RHFont.familyHeading,
                fontSize: RHFont.sizeXXLarge,
                fontWeight: RHFont.weightSemibold,
                lineHeight: RHFont.lineHeightHeading
            }
        })
        this._headingLabel.viewHTMLElement.tabIndex = -1
        this.addRow(this._headingLabel, 0)
    }

}


// Small, consistent line icons for process steps. The icons are supplementary:
// every step retains its title and result text.
class HelpFlowIconView extends UITextView {

    static readonly _paths: Record<HelpFlowIconName, string> = {
        search: '<circle cx="11" cy="11" r="7"></circle><path d="m20 20-4-4"></path>',
        visibility: '<path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12"></path><circle cx="12" cy="12" r="2.5"></circle>',
        register: '<path d="M15 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="8.5" cy="7" r="4"></circle><path d="M19 8v6M16 11h6"></path>',
        edit: '<path d="M12 20h9"></path><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4Z"></path>',
        send: '<path d="m22 2-7 20-4-9-9-4Z"></path><path d="M22 2 11 13"></path>',
        message: '<path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4Z"></path><path d="M8 9h8M8 13h5"></path>',
        deadline: '<circle cx="12" cy="12" r="9"></circle><path d="M12 7v5l3 2"></path>',
        open: '<rect x="3" y="8" width="18" height="12" rx="2"></rect><path d="M7 8V6a5 5 0 0 1 9.5-2"></path>',
        evaluate: '<path d="M12 3v18M5 7h14M7 7l-4 7h8ZM17 7l-4 7h8ZM8 21h8"></path>',
        winner: '<path d="M8 21h8M12 17v4"></path><path d="M7 4h10v4a5 5 0 0 1-10 0Z"></path><path d="M7 6H3v2a4 4 0 0 0 4 4M17 6h4v2a4 4 0 0 1-4 4"></path>',
        contract: '<path d="M6 2h9l4 4v16H6Z"></path><path d="M14 2v5h5M9 12h6M9 16h6"></path>',
        document: '<path d="M6 2h9l4 4v16H6Z"></path><path d="M14 2v5h5M9 11h6M9 15h6M9 19h4"></path>',
        team: '<path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="8.5" cy="7" r="4"></circle><path d="M20 8v6M17 11h6"></path>',
        preview: '<path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12"></path><path d="m10 12 1.5 1.5L15 10"></path>'
    }

    constructor(iconName: HelpFlowIconName) {
        super()
        ;(this as HelpFlowIconView).configureWithObject({
            text: HelpFlowIconView._markup(iconName),
            textColor: RHColors.primaryBlue,
            textAlignment: UITextView.textAlignment.center,
            userInteractionEnabled: NO,
            style: {
                lineHeight: `${RHSpacing.large}px`
            }
        })
    }

    static _markup(iconName: HelpFlowIconName): string {
        return '<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" ' +
            'fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" ' +
            `stroke-linejoin="round" aria-hidden="true">${HelpFlowIconView._paths[iconName]}</svg>`
    }

}


// One process step with an icon, action label and short outcome.
class HelpFlowStepView extends UIView {

    readonly _iconView: HelpFlowIconView
    readonly _titleLabel: UITextView
    readonly _detailLabel: UITextView

    constructor(step: HelpFlowStep) {
        super()
        ;(this as HelpFlowStepView).configureWithObject({
            backgroundColor: RHColors.primaryBlueSurfaceFaint,
            style: {
                borderRadius: `${RHRadius.medium}px`,
                border: `1px solid ${RHColors.borderLight.stringValue}`
            }
        })
        this._iconView = new HelpFlowIconView(step.icon).addedAsSubviewToView(this)
        this._titleLabel = new UITextView().configuredWithObject({
            text: step.title,
            textColor: RHColors.textHeading,
            textAlignment: UITextView.textAlignment.center,
            isSingleLine: NO,
            style: {
                fontFamily: RHFont.familyBase,
                fontSize: RHFont.sizeSmall,
                fontWeight: RHFont.weightSemibold,
                lineHeight: RHFont.lineHeightHeading
            }
        }).addedAsSubviewToView(this)
        this._detailLabel = new UITextView().configuredWithObject({
            text: step.detail,
            textColor: RHColors.textMuted,
            textAlignment: UITextView.textAlignment.center,
            isSingleLine: NO,
            style: {
                fontFamily: RHFont.familyBase,
                fontSize: RHFont.sizeXSmall,
                fontWeight: RHFont.weightRegular,
                lineHeight: RHFont.lineHeightBase
            }
        }).addedAsSubviewToView(this)
    }

    override layoutSubviews() {
        super.layoutSubviews()
        const bounds = this.contentBoundsWithInset(RHSpacing.small)
        let row = bounds.rectangleWithHeight(RHSpacing.large)
        this._iconView.frame = row.rectangleWithWidth(RHSpacing.large, 0.5)
        row = row.rectangleForNextRow(RHSpacing.small, this._titleLabel)
        this._titleLabel.frame = row
        row = row.rectangleForNextRow(RHSpacing.tiny, this._detailLabel)
        this._detailLabel.frame = row
    }

    override intrinsicContentHeight(constrainingWidth: number = this.bounds.width): number {
        const contentWidth = Math.max(1, constrainingWidth - RHSpacing.small * 2)
        return RHSpacing.small * 2 +
            RHSpacing.large +
            RHSpacing.small +
            this._titleLabel.intrinsicContentHeight(contentWidth) +
            RHSpacing.tiny +
            this._detailLabel.intrinsicContentHeight(contentWidth)
    }

}


// Responsive process graphic. On a wide card the steps form a horizontal flow;
// on a narrow card they become a readable vertical timeline.
class HelpFlowDiagramView extends UIView {

    readonly _steps: HelpFlowStepView[] = []
    readonly _arrows: UITextView[] = []
    static readonly _compactBreakpoint = RHSpacing.xxLarge * 14

    constructor(steps: HelpFlowStep[]) {
        super()
        for (let index = 0; index < steps.length; index++) {
            this._steps.push(
                new HelpFlowStepView(steps[index]).addedAsSubviewToView(this)
            )
            if (index < steps.length - 1) {
                this._arrows.push(
                    new UITextView().configuredWithObject({
                        textColor: RHColors.textPlaceholder,
                        textAlignment: UITextView.textAlignment.center,
                        userInteractionEnabled: NO,
                        style: {
                            fontFamily: RHFont.familyBase,
                            fontSize: RHFont.sizeXXLarge,
                            fontWeight: RHFont.weightRegular
                        }
                    }).addedAsSubviewToView(this)
                )
                this._arrows.lastElement.viewHTMLElement.setAttribute("aria-hidden", "true")
            }
        }
    }

    override layoutSubviews() {
        super.layoutSubviews()
        const bounds = this.contentBoundsWithInset(0)
        IF(bounds.width < HelpFlowDiagramView._compactBreakpoint)(() => {
            this._layoutCompact(bounds)
        }).ELSE(() => {
            this._layoutWide(bounds)
        })
    }

    _layoutWide(bounds: ReturnType<UIView["contentBoundsWithInset"]>) {
        const arrowWidth = RHSpacing.large
        const gap = RHSpacing.tiny
        const arrowCount = this._arrows.length
        const stepCount = this._steps.length
        const stepsWidth = bounds.width - arrowWidth * arrowCount - gap * (stepCount + arrowCount - 1)
        const stepWidth = stepsWidth / stepCount
        let columnFrame = bounds.rectangleWithWidth(stepWidth)
        this._steps[0].frame = columnFrame
        for (let index = 1; index < stepCount; index++) {
            const arrowFrame = columnFrame.rectangleForNextColumn(gap, arrowWidth)
            this._arrows[index - 1].text = "→"
            this._arrows[index - 1].frame = arrowFrame.rectangleWithHeight(RHSpacing.xLarge, 0.5)
            columnFrame = arrowFrame.rectangleForNextColumn(gap, stepWidth)
            this._steps[index].frame = columnFrame
        }
    }

    _layoutCompact(bounds: ReturnType<UIView["contentBoundsWithInset"]>) {
        const arrowHeight = RHSpacing.medium
        let row = bounds.rectangleWithHeight(this._steps[0].intrinsicContentHeight(bounds.width))
        this._steps[0].frame = row
        for (let index = 1; index < this._steps.length; index++) {
            row = row.rectangleForNextRow(RHSpacing.tiny, arrowHeight)
            this._arrows[index - 1].text = "↓"
            this._arrows[index - 1].frame = row
            row = row.rectangleForNextRow(
                RHSpacing.tiny,
                this._steps[index].intrinsicContentHeight(bounds.width)
            )
            this._steps[index].frame = row
        }
    }

    override intrinsicContentHeight(constrainingWidth: number = this.bounds.width): number {
        if (this._steps.length === 0) {
            return 0
        }
        if (constrainingWidth < HelpFlowDiagramView._compactBreakpoint) {
            let totalHeight = 0
            for (const step of this._steps) {
                totalHeight = totalHeight + step.intrinsicContentHeight(constrainingWidth)
            }
            totalHeight = totalHeight + this._arrows.length * (RHSpacing.medium + RHSpacing.tiny * 2)
            return totalHeight
        }

        const arrowWidth = RHSpacing.large
        const gap = RHSpacing.tiny
        const stepCount = this._steps.length
        const stepWidth = (
            constrainingWidth -
            arrowWidth * this._arrows.length -
            gap * (stepCount + this._arrows.length - 1)
        ) / stepCount
        let maximumHeight = 0
        for (const step of this._steps) {
            maximumHeight = Math.max(maximumHeight, step.intrinsicContentHeight(stepWidth))
        }
        return maximumHeight
    }

}


// A bullet row with a hanging indent.
class HelpBulletRowView extends UIView {

    readonly _bulletLabel: UITextView
    readonly _textLabel: UITextView
    static readonly _bulletColumnWidth = RHSpacing.medium

    constructor(text: string) {
        super()
        this._bulletLabel = new UITextView().configuredWithObject({
            text: "•",
            textColor: RHColors.primaryBlue,
            style: {
                fontFamily: RHFont.familyBase,
                fontSize: RHFont.sizeMedium,
                fontWeight: RHFont.weightBold,
                lineHeight: RHFont.lineHeightBase
            }
        }).addedAsSubviewToView(this)
        this._textLabel = new UITextView().configuredWithObject({
            text: text,
            textColor: RHColors.textPrimary,
            isSingleLine: NO,
            style: {
                fontFamily: RHFont.familyBase,
                fontSize: RHFont.sizeMedium,
                fontWeight: RHFont.weightRegular,
                lineHeight: RHFont.lineHeightBase
            }
        }).addedAsSubviewToView(this)
    }

    override layoutSubviews() {
        super.layoutSubviews()
        const bounds = this.contentBoundsWithInset(0)
        this._bulletLabel.frame = bounds.rectangleWithWidth(HelpBulletRowView._bulletColumnWidth)
        this._textLabel.frame = bounds.rectangleWithInsets(
            HelpBulletRowView._bulletColumnWidth,
            0,
            0,
            0
        )
    }

    override intrinsicContentHeight(constrainingWidth: number = this.bounds.width): number {
        return this._textLabel.intrinsicContentHeight(
            constrainingWidth - HelpBulletRowView._bulletColumnWidth
        )
    }

}


// An ordered-step row with a hanging number column.
class HelpNumberedRowView extends UIView {

    readonly _numberLabel: UITextView
    readonly _textLabel: UITextView
    static readonly _numberColumnWidth = RHSpacing.large + RHSpacing.tiny

    constructor(stepNumber: number, text: string) {
        super()
        this._numberLabel = new UITextView().configuredWithObject({
            text: `${stepNumber}.`,
            textColor: RHColors.primaryBlue,
            style: {
                fontFamily: RHFont.familyBase,
                fontSize: RHFont.sizeMedium,
                fontWeight: RHFont.weightSemibold,
                lineHeight: RHFont.lineHeightBase
            }
        }).addedAsSubviewToView(this)
        this._textLabel = new UITextView().configuredWithObject({
            text: text,
            textColor: RHColors.textPrimary,
            isSingleLine: NO,
            style: {
                fontFamily: RHFont.familyBase,
                fontSize: RHFont.sizeMedium,
                fontWeight: RHFont.weightRegular,
                lineHeight: RHFont.lineHeightBase
            }
        }).addedAsSubviewToView(this)
    }

    override layoutSubviews() {
        super.layoutSubviews()
        const bounds = this.contentBoundsWithInset(0)
        this._numberLabel.frame = bounds.rectangleWithWidth(HelpNumberedRowView._numberColumnWidth)
        this._textLabel.frame = bounds.rectangleWithInsets(
            HelpNumberedRowView._numberColumnWidth,
            0,
            0,
            0
        )
    }

    override intrinsicContentHeight(constrainingWidth: number = this.bounds.width): number {
        return this._textLabel.intrinsicContentHeight(
            constrainingWidth - HelpNumberedRowView._numberColumnWidth
        )
    }

}


// A semantic in-app link with a short explanation of where it leads.
class HelpNavigationLinkView extends UIView {

    readonly _link: UILink
    readonly _linkMeasurementLabel: UITextView
    readonly _descriptionLabel: UITextView
    static readonly _minimumLinkHeight = RHSpacing.large

    constructor(
        linkText: string,
        description: string,
        targetRouteForCurrentState: () => UIRoute
    ) {
        super()
        ;(this as HelpNavigationLinkView).configureWithObject({
            backgroundColor: RHColors.primaryBlueSurfaceFaint,
            style: {
                border: `1px solid ${RHColors.borderLight.stringValue}`,
                borderRadius: `${RHRadius.medium}px`
            }
        })
        this._link = new UILink().configuredWithObject({
            text: `${linkText} →`,
            targetRouteForCurrentState: targetRouteForCurrentState,
            style: {
                color: RHColors.primaryBlue.stringValue,
                fontFamily: RHFont.familyBase,
                fontSize: RHFont.sizeMedium,
                fontWeight: RHFont.weightSemibold,
                lineHeight: RHFont.lineHeightBase,
                textDecoration: "underline",
                textUnderlineOffset: "3px"
            }
        }).addedAsSubviewToView(this)
        this._linkMeasurementLabel = new UITextView().configuredWithObject({
            text: `${linkText} →`,
            isSingleLine: NO,
            style: {
                fontFamily: RHFont.familyBase,
                fontSize: RHFont.sizeMedium,
                fontWeight: RHFont.weightSemibold,
                lineHeight: RHFont.lineHeightBase
            }
        })
        this._descriptionLabel = new UITextView().configuredWithObject({
            text: description,
            textColor: RHColors.textMuted,
            isSingleLine: NO,
            userInteractionEnabled: NO,
            style: {
                fontFamily: RHFont.familyBase,
                fontSize: RHFont.sizeSmall,
                fontWeight: RHFont.weightRegular,
                lineHeight: RHFont.lineHeightBase
            }
        }).addedAsSubviewToView(this)
    }

    override layoutSubviews() {
        super.layoutSubviews()
        const bounds = this.contentBoundsWithInset(RHSpacing.medium)
        const linkHeight = Math.max(
            HelpNavigationLinkView._minimumLinkHeight,
            this._linkMeasurementLabel.intrinsicContentHeight(bounds.width)
        )
        let row = bounds.rectangleWithHeight(linkHeight)
        this._link.frame = row
        row = row.rectangleForNextRow(RHSpacing.tiny, this._descriptionLabel)
        this._descriptionLabel.frame = row
    }

    override intrinsicContentHeight(constrainingWidth: number = this.bounds.width): number {
        const contentWidth = Math.max(1, constrainingWidth - RHSpacing.medium * 2)
        const linkHeight = Math.max(
            HelpNavigationLinkView._minimumLinkHeight,
            this._linkMeasurementLabel.intrinsicContentHeight(contentWidth)
        )
        return RHSpacing.medium * 2 +
            linkHeight +
            RHSpacing.tiny +
            this._descriptionLabel.intrinsicContentHeight(contentWidth)
    }

}


// A route-backed TOC row: keyboard accessible, bookmarkable and compatible
// with browser Back/Forward navigation. The whole softly highlighted row is
// actionable, so a permanent text underline is not needed as an affordance.
class HelpTableOfContentsRowView extends UILink {

    readonly _titleLabel: UITextView
    static readonly _minimumHeight = RHSpacing.xLarge + RHSpacing.small
    static readonly _horizontalInset = RHSpacing.medium
    static readonly _verticalInset = RHSpacing.small

    constructor(text: string, targetRouteForCurrentState: () => UIRoute) {
        super()
        ;(this as HelpTableOfContentsRowView).configureWithObject({
            backgroundColor: RHColors.transparent,
            targetRouteForCurrentState: targetRouteForCurrentState,
            style: {
                borderRadius: `${RHRadius.medium}px`,
                outline: "none",
                outlineOffset: RHButtons.focusOutlineOffset,
                textDecoration: "none",
                transition: `background-color ${RHTransition.fast}, outline-color ${RHTransition.fast}`
            }
        })
        this.viewHTMLElement.setAttribute("aria-label", text)
        this._titleLabel = new UITextView().configuredWithObject({
            text: `${text}\u00A0›`,
            textColor: RHColors.primaryBlue,
            isSingleLine: NO,
            userInteractionEnabled: NO,
            style: {
                fontFamily: RHFont.familyBase,
                fontSize: RHFont.sizeMedium,
                fontWeight: RHFont.weightMedium,
                lineHeight: RHFont.lineHeightBase,
                transition: `color ${RHTransition.fast}`
            }
        }).addedAsSubviewToView(this)
        this.updateContentForCurrentState()
    }

    override layoutSubviews() {
        super.layoutSubviews()
        const bounds = this.contentBoundsWithInsets(
            HelpTableOfContentsRowView._horizontalInset,
            HelpTableOfContentsRowView._horizontalInset,
            HelpTableOfContentsRowView._verticalInset,
            HelpTableOfContentsRowView._verticalInset
        )
        this._titleLabel.frame = bounds
    }

    override intrinsicContentHeight(constrainingWidth: number = this.bounds.width): number {
        const titleWidth = Math.max(
            1,
            constrainingWidth -
                HelpTableOfContentsRowView._horizontalInset * 2
        )
        return Math.max(
            HelpTableOfContentsRowView._minimumHeight,
            HelpTableOfContentsRowView._verticalInset * 2 +
                this._titleLabel.intrinsicContentHeight(titleWidth)
        )
    }

    override updateContentForNormalState() {
        super.updateContentForNormalState()
        ;(this as HelpTableOfContentsRowView).configureWithObject({
            backgroundColor: RHColors.transparent,
            style: { outline: "none" }
        })
        this._titleLabel.configureWithObject({
            textColor: RHColors.primaryBlue
        })
    }

    override updateContentForHoveredState() {
        super.updateContentForHoveredState()
        ;(this as HelpTableOfContentsRowView).configureWithObject({
            backgroundColor: RHColors.menuHoverBlueSurface,
            style: { outline: "none" }
        })
        this._titleLabel.configureWithObject({
            textColor: RHColors.primaryBlueHover
        })
    }

    override updateContentForHighlightedState() {
        super.updateContentForHighlightedState()
        ;(this as HelpTableOfContentsRowView).configureWithObject({
            backgroundColor: RHColors.primaryBlueSurface,
            style: { outline: "none" }
        })
        this._titleLabel.configureWithObject({
            textColor: RHColors.primaryBlueActive
        })
    }

    override updateContentForFocusedState() {
        super.updateContentForFocusedState()
        ;(this as HelpTableOfContentsRowView).configureWithObject({
            backgroundColor: RHColors.primaryBlueSurfaceFaint,
            style: { outline: RHButtons.focusOutline }
        })
        this._titleLabel.configureWithObject({
            textColor: RHColors.primaryBlue
        })
    }

}


// A compact responsive matrix row. Wide screens get three columns; narrow
// screens get a labelled vertical record so no information is squeezed away.
class HelpMatrixRowView extends UIView {

    readonly _eventLabel: UITextView
    readonly _recipientLabel: UITextView
    readonly _channelLabel: UITextView
    readonly _eventText: string
    readonly _recipientText: string
    readonly _channelText: string
    readonly _isHeader: boolean
    static readonly _compactBreakpoint = RHSpacing.xxLarge * 11

    constructor(
        eventText: string,
        recipientText: string,
        channelText: string,
        isHeader: boolean,
        isShaded: boolean
    ) {
        super()
        this._eventText = eventText
        this._recipientText = recipientText
        this._channelText = channelText
        this._isHeader = isHeader
        let backgroundColor = RHColors.white
        if (isHeader) {
            backgroundColor = RHColors.primaryBlueSurface
        }
        else if (isShaded) {
            backgroundColor = RHColors.primaryBlueSurfaceFaint
        }
        ;(this as HelpMatrixRowView).configureWithObject({
            backgroundColor: backgroundColor,
            style: {
                borderBottom: `1px solid ${RHColors.borderLight.stringValue}`
            }
        })
        this.viewHTMLElement.setAttribute("role", "row")
        this._eventLabel = this._matrixLabel(eventText, isHeader).addedAsSubviewToView(this)
        this._recipientLabel = this._matrixLabel(recipientText, isHeader).addedAsSubviewToView(this)
        this._channelLabel = this._matrixLabel(channelText, isHeader).addedAsSubviewToView(this)
        const cellRole = IF(isHeader)(() => "columnheader").ELSE(() => "cell")
        this._eventLabel.viewHTMLElement.setAttribute("role", cellRole)
        this._recipientLabel.viewHTMLElement.setAttribute("role", cellRole)
        this._channelLabel.viewHTMLElement.setAttribute("role", cellRole)
    }

    _matrixLabel(text: string, isHeader: boolean): UITextView {
        let fontWeight: string = RHFont.weightRegular
        let textColor = RHColors.textPrimary
        if (isHeader) {
            fontWeight = RHFont.weightSemibold
            textColor = RHColors.textHeading
        }
        return new UITextView().configuredWithObject({
            text: text,
            textColor: textColor,
            isSingleLine: NO,
            style: {
                fontFamily: RHFont.familyBase,
                fontSize: RHFont.sizeSmall,
                fontWeight: fontWeight,
                lineHeight: RHFont.lineHeightBase
            }
        })
    }

    _configureTextForWidth(width: number) {
        if (width < HelpMatrixRowView._compactBreakpoint && !this._isHeader) {
            this._eventLabel.text = `Sündmus: ${this._eventText}`
            this._recipientLabel.text = `Saaja: ${this._recipientText}`
            this._channelLabel.text = `Kanal: ${this._channelText}`
            return
        }
        this._eventLabel.text = this._eventText
        this._recipientLabel.text = this._recipientText
        this._channelLabel.text = this._channelText
    }

    override layoutSubviews() {
        super.layoutSubviews()
        const bounds = this.contentBoundsWithInset(RHSpacing.small)
        this._configureTextForWidth(bounds.width)
        IF(bounds.width < HelpMatrixRowView._compactBreakpoint)(() => {
            IF(this._isHeader)(() => {
                this._eventLabel.frame = bounds.rectangleWithWidth(bounds.width * 0.32)
                this._recipientLabel.frame = bounds
                    .rectangleWithInsets(bounds.width * 0.32, 0, 0, 0)
                    .rectangleWithWidth(bounds.width * 0.36)
                this._channelLabel.frame = bounds.rectangleWithWidth(bounds.width * 0.32, 1)
            }).ELSE(() => {
                let row = bounds.rectangleWithHeight(this._eventLabel)
                this._eventLabel.frame = row
                row = row.rectangleForNextRow(RHSpacing.tiny, this._recipientLabel)
                this._recipientLabel.frame = row
                row = row.rectangleForNextRow(RHSpacing.tiny, this._channelLabel)
                this._channelLabel.frame = row
            })
        }).ELSE(() => {
            const eventWidth = bounds.width * 0.27
            const recipientWidth = bounds.width * 0.44
            const channelWidth = bounds.width - eventWidth - recipientWidth - RHSpacing.medium * 2
            let column = bounds.rectangleWithWidth(eventWidth)
            this._eventLabel.frame = column
            column = column.rectangleForNextColumn(RHSpacing.medium, recipientWidth)
            this._recipientLabel.frame = column
            column = column.rectangleForNextColumn(RHSpacing.medium, channelWidth)
            this._channelLabel.frame = column
        })
    }

    override intrinsicContentHeight(constrainingWidth: number = this.bounds.width): number {
        const contentWidth = Math.max(1, constrainingWidth - RHSpacing.small * 2)
        this._configureTextForWidth(contentWidth)
        if (contentWidth < HelpMatrixRowView._compactBreakpoint) {
            if (this._isHeader) {
                const headerContentWidth = Math.max(1, contentWidth * 0.32)
                return RHSpacing.small * 2 + Math.max(
                    this._eventLabel.intrinsicContentHeight(headerContentWidth),
                    this._recipientLabel.intrinsicContentHeight(headerContentWidth),
                    this._channelLabel.intrinsicContentHeight(headerContentWidth)
                )
            }
            return RHSpacing.small * 2 +
                this._eventLabel.intrinsicContentHeight(contentWidth) +
                RHSpacing.tiny +
                this._recipientLabel.intrinsicContentHeight(contentWidth) +
                RHSpacing.tiny +
                this._channelLabel.intrinsicContentHeight(contentWidth)
        }
        const eventWidth = contentWidth * 0.27
        const recipientWidth = contentWidth * 0.44
        const channelWidth = contentWidth - eventWidth - recipientWidth - RHSpacing.medium * 2
        return RHSpacing.small * 2 + Math.max(
            this._eventLabel.intrinsicContentHeight(eventWidth),
            this._recipientLabel.intrinsicContentHeight(recipientWidth),
            this._channelLabel.intrinsicContentHeight(channelWidth)
        )
    }

}


// Owns a contiguous set of matrix rows so assistive technology receives valid
// table, row and cell relationships without changing the responsive layout.
class HelpMatrixView extends HelpStackView {

    constructor(accessibilityLabel: string) {
        super()
        this.viewHTMLElement.setAttribute("role", "table")
        this.viewHTMLElement.setAttribute("aria-label", accessibilityLabel)
    }

    addMatrixRow(row: HelpMatrixRowView): this {
        this.addRow(row, 0)
        return this
    }

}


// ============================================================================
// Help view controller
// ============================================================================


export class HelpViewController extends UIViewController {

    static override readonly routeComponentName = "help"

    static readonly _isUnderMaintenance = YES

    static override readonly ParameterIdentifierName = {
        section: "section"
    }

    readonly backButton = new RHButton().configuredWithObject({
        titleLabel: { text: "← Tagasi" },
        variant: "quietWarning",
        contentPadding: RHButtons.compactContentPadding,
        style: { fontSize: RHButtons.compactFontSize },
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: () => {
                this._goBack()
            }
        }
    }).addedAsSubviewToView(this.view)

    readonly titleLabel = new UITextView(undefined, UITextView.type.header2).configuredWithObject({
        text: "Abi ja kasutusjuhend",
        textColor: RHColors.textHeading,
        isSingleLine: NO,
        style: {
            fontFamily: RHFont.familyHeading,
            fontSize: RHFont.sizeXXXLarge,
            fontWeight: RHFont.weightBold,
            lineHeight: RHFont.lineHeightHeading
        }
    }).addedAsSubviewToView(this.view)

    readonly introLabel = new UITextView().configuredWithObject({
        text: "Praktiline juhend hanke korraldamiseks ja pakkumuse esitamiseks. Siit leiad tegevuste " +
            "järjekorra, nähtavuse, rollide ja teavituste selgitused ning otseteed vajalikele vaadetele.",
        textColor: RHColors.textMuted,
        isSingleLine: NO,
        style: {
            fontFamily: RHFont.familyBase,
            fontSize: RHFont.sizeLarge,
            fontWeight: RHFont.weightRegular,
            lineHeight: RHFont.lineHeightBase
        }
    }).addedAsSubviewToView(this.view)

    readonly _tableOfContentsCard = new HelpCardView("Sisukord")
    readonly _startCard = new HelpCardView("Alusta siit")

    readonly _tendererDiscoveryCard = new HelpCardView("Pakkujale · hanke leidmine ja hankes osalemine")
    readonly _tendererOfferCard = new HelpCardView("Pakkujale · pakkumuse koostamine ja esitamine")
    readonly _tendererResultCard = new HelpCardView("Pakkujale · hindamine, tulemus ja leping")

    readonly _procurerCreateCard = new HelpCardView("Hankijale · ettevalmistamisest eelvaateni")
    readonly _procurerPublishCard = new HelpCardView("Hankijale · avaldamine ja nähtavus")
    readonly _procurerEvaluationCard = new HelpCardView("Hankijale · pakkumuste avamisest tulemuse ja lepinguni")

    readonly _teamCard = new HelpCardView("Meeskonnad, rollid ja õigused")
    readonly _messagingCard = new HelpCardView("Teabevahetus: avalik ja privaatne")
    readonly _deadlineCard = new HelpCardView("Tähtaja muutmine ja automaatne pikenemine")
    readonly _notificationsCard = new HelpCardView("Teavitused: saajad, aeg ja kanalid")
    readonly _companyCard = new HelpCardView("Ettevõtte haldus ja CPV-valikud")
    readonly _statusCard = new HelpCardView("Staatused ja teavituste märgid")
    readonly _faqCard = new HelpCardView("Probleemide lahendamine ja korduma kippuvad küsimused")

    readonly _cards: HelpCardView[] = [
        this._tableOfContentsCard,
        this._startCard,
        this._tendererDiscoveryCard,
        this._tendererOfferCard,
        this._tendererResultCard,
        this._procurerCreateCard,
        this._procurerPublishCard,
        this._procurerEvaluationCard,
        this._teamCard,
        this._messagingCard,
        this._deadlineCard,
        this._notificationsCard,
        this._companyCard,
        this._statusCard,
        this._faqCard
    ]

    readonly _cardsBySectionIdentifier: Record<string, HelpCardView> = {
        start: this._startCard,
        "tenderer-discovery": this._tendererDiscoveryCard,
        "tenderer-offer": this._tendererOfferCard,
        "tenderer-result": this._tendererResultCard,
        "procurer-create": this._procurerCreateCard,
        "procurer-publish": this._procurerPublishCard,
        "procurer-evaluation": this._procurerEvaluationCard,
        teams: this._teamCard,
        messaging: this._messagingCard,
        deadlines: this._deadlineCard,
        notifications: this._notificationsCard,
        company: this._companyCard,
        statuses: this._statusCard,
        faq: this._faqCard
    }

    override _focusManager = new RHFocusManager(() => {
        const focusableItems: UIView[] = [this.backButton]
        for (const card of this._cards.filter(card => this.view.hasSubview(card))) {
            for (const row of card._rows) {
                if (row.view instanceof HelpTableOfContentsRowView) {
                    focusableItems.push(row.view)
                }
                else if (row.view instanceof HelpNavigationLinkView) {
                    focusableItems.push(row.view._link)
                }
            }
        }
        return focusableItems
    })

    _lastHandledSectionIdentifier = ""


    constructor(view: UIView) {
        super(view)
        this.view.backgroundColor = RHColors.pageBackground
        if (HelpViewController._isUnderMaintenance) {
            this.introLabel.text = "Abileht on hetkel uuendamisel"
            return
        }
        for (const card of this._cards) {
            card.addedAsSubviewToView(this.view)
        }
        this._buildTableOfContents()
        this._buildStartCard()
        this._buildTendererDiscoveryCard()
        this._buildTendererOfferCard()
        this._buildTendererResultCard()
        this._populateProcurementCreationHelpCard()
        this._buildProcurerPublishCard()
        this._buildProcurerEvaluationCard()
        this._buildTeamCard()
        this._buildMessagingCard()
        this._buildDeadlineCard()
        this._buildNotificationsCard()
        this._buildCompanyCard()
        this._buildStatusCard()
        this._buildFaqCard()
    }

    override async viewWillAppear() {
        await super.viewWillAppear()
        RHPageFocusManager.shared.registerContentControllerManager(this._focusManager)
    }

    override async viewWillDisappear() {
        await super.viewWillDisappear()
        RHPageFocusManager.shared.deregisterContentControllerManager(this._focusManager)
    }


    // MARK: - Routing

    override async handleRoute(route: UIRoute) {
        await super.handleRoute(route)
        const sectionIdentifier = route
            .componentWithViewController(HelpViewController)
            ?.parameters[HelpViewController.ParameterIdentifierName.section]
        if (!sectionIdentifier) {
            this._lastHandledSectionIdentifier = ""
            return
        }
        if (sectionIdentifier === this._lastHandledSectionIdentifier) {
            return
        }
        const card = this._cardsBySectionIdentifier[sectionIdentifier]
        if (!card || !this.view.hasSubview(card)) {
            return
        }
        this._lastHandledSectionIdentifier = sectionIdentifier
        window.requestAnimationFrame(() => this._scrollToCard(card))
    }


    // MARK: - Content builders

    _buildTableOfContents() {
        this._tableOfContentsCard.viewHTMLElement.setAttribute("role", "navigation")
        this._tableOfContentsCard.viewHTMLElement.setAttribute("aria-label", "Abilehe sisukord")
        this._tableOfContentsCard.addRow(
            this._paragraph(
                "Vali juhendi osa oma rolli järgi. Ühiste teemade alt leiad teabe õiguste, teabevahetuse, " +
                "tähtaegade ja teavituste kohta. Iga jaotise lingi saad kopeerida või brauseris " +
                "järjehoidjaks salvestada."
            ),
            RHSpacing.medium
        )

        const groups: { title: string; entries: HelpTableOfContentsEntry[] }[] = [
            {
                title: "ALUSTAMINE",
                entries: [
                    { text: "Alustamine ja kiirlingid", sectionIdentifier: "start", card: this._startCard }
                ]
            },
            {
                title: "PAKKUJALE",
                entries: [
                    {
                        text: "Hanke leidmine, nähtavus ja registreerumine",
                        sectionIdentifier: "tenderer-discovery",
                        card: this._tendererDiscoveryCard
                    },
                    {
                        text: "Pakkumuse koostamine, esitamine ja muutmine",
                        sectionIdentifier: "tenderer-offer",
                        card: this._tendererOfferCard
                    },
                    {
                        text: "Hindamine, tulemus ja leping",
                        sectionIdentifier: "tenderer-result",
                        card: this._tendererResultCard
                    }
                ]
            },
            {
                title: "HANKIJALE",
                entries: [
                    {
                        text: "Ettevalmistamisest eelvaateni",
                        sectionIdentifier: "procurer-create",
                        card: this._procurerCreateCard
                    },
                    {
                        text: "Hanke avaldamine ja nähtavuse määramine",
                        sectionIdentifier: "procurer-publish",
                        card: this._procurerPublishCard
                    },
                    {
                        text: "Pakkumuste avamisest tulemuse ja lepinguni",
                        sectionIdentifier: "procurer-evaluation",
                        card: this._procurerEvaluationCard
                    }
                ]
            },
            {
                title: "ÜHISED TEEMAD",
                entries: [
                    { text: "Meeskonnad, rollid ja õigused", sectionIdentifier: "teams", card: this._teamCard },
                    { text: "Teabevahetus", sectionIdentifier: "messaging", card: this._messagingCard },
                    { text: "Tähtaja muutmine ja pikenemine", sectionIdentifier: "deadlines", card: this._deadlineCard },
                    { text: "Teavitused: saajad ja kanalid", sectionIdentifier: "notifications", card: this._notificationsCard },
                    { text: "Staatused ja teavituste märgid", sectionIdentifier: "statuses", card: this._statusCard }
                ]
            },
            {
                title: "ETTEVÕTE JA TUGI",
                entries: [
                    { text: "Ettevõtte haldus ja CPV-valikud", sectionIdentifier: "company", card: this._companyCard },
                    { text: "Probleemide lahendamine ja KKK", sectionIdentifier: "faq", card: this._faqCard }
                ]
            }
        ]

        for (const group of groups) {
            this._tableOfContentsCard.addRow(this._eyebrow(group.title), RHSpacing.large)
            for (const entry of group.entries) {
                this._tableOfContentsCard.addRow(
                    new HelpTableOfContentsRowView(
                        entry.text,
                        () => this._routeForHelpSection(entry.sectionIdentifier)
                    ),
                    RHSpacing.tiny
                )
            }
        }
    }

    _buildStartCard() {
        this._startCard.addRow(
            this._paragraph(
                "Ülariba vaated ja õigused lähtuvad aktiivsest ettevõttest. Kuvada võivad „Hankija“, " +
                "„Pakkuja“ ja „Audiitor“ vastavalt sinu ettevõtterollidele ning sellele, millistes rollides " +
                "ettevõte saab tegutseda. Kui vaateid on mitu, saad nende vahel ülaribal vahetada. " +
                "„Seaded“ ja „Abi“ leiad kasutaja nime alt avanevast menüüst."
            ),
            RHSpacing.medium
        )
        this._startCard.addRow(
            new RHCalloutView(
                "info",
                "Mitme ettevõttega kasutaja",
                "Kui sul on aktiivne liikmesus mitmes ettevõttes, vali aktiivne ettevõte kasutajamenüüst. " +
                "Vahetamine muudab ettevõttega seotud vaateid, õigusi ja andmeid. Töölaual, mustandites, " +
                "teatistes ja tegevustes saad vajaduse korral kasutada filtrit „Kõik minu ettevõtted“."
            ),
            RHSpacing.large
        )
        this._startCard.addRow(
            new RHCalloutView(
                "info",
                "Hankija vaade",
                "„Töölaualt“ leiad avaldatud ja ettevalmistamisel hanked ning saad alustada uut hanget. " +
                "Hanke loomise ja muutmise võimalused sõltuvad sinu ettevõtte- ja hankemeeskonna rollist."
            ),
            RHSpacing.small
        )
        this._startCard.addRow(
            new RHCalloutView(
                "info",
                "Pakkuja vaade",
                "Vaatest „Hanked“ leiad avalikud hanked ja sinu ettevõttele nähtavad mitteavalikud hanked. Kui " +
                "avaldatakse sinu CPV-valikule vastav avalik hange, võib menüüvaliku „Hanked“ juures " +
                "olla rasvane kiri. Samas vaates näed ettevõtte osalemise ja tulemuse staatust."
            ),
            RHSpacing.small
        )
        this._startCard.addRow(
            new RHCalloutView(
                "info",
                "Audiitori vaade",
                "Audiitor saab aktiivse ettevõtte avaldatud hangete üksikasju, pärast pakkumuste avamist " +
                "pakkumusi ja lepinguid vaadata, kuid ei näe mustandeid ega saa andmeid muuta. Menüüvalikud „Otsing“, " +
                "„Teavitused“ ja „Avalik statistika“ jäävad kättesaadavaks."
            ),
            RHSpacing.small
        )
        this._startCard.addRow(this._subheading("Kiirlingid"), RHSpacing.large)
        this._startCard.addRow(
            new HelpNavigationLinkView(
                "Ava pakkuja vaade „Hanked“",
                "Avab pakkuja vaate „Hanked“, kui see vaade on aktiivse ettevõtte rollidega lubatud.",
                () => this._userMainRoute("offerer")
            ),
            RHSpacing.small
        )
        this._startCard.addRow(
            new HelpNavigationLinkView(
                "Ava hankija töölaud",
                "Avab hankija töölaual vahekaardi „Minu hanked“, kui Hankija vaade on aktiivse ettevõttega lubatud.",
                () => this._procurerMainRoute("published")
            ),
            RHSpacing.small
        )
        this._startCard.addRow(
            new HelpNavigationLinkView(
                "Ava hankeotsing",
                "Otsi hankeid nimetuse või viitenumbri järgi ning filtreeri tulemusi staatuse, eeldatava " +
                "maksumuse, avaldamise kuupäeva, tähtaja või CPV-koodi järgi.",
                () => this._searchRoute()
            ),
            RHSpacing.small
        )
        this._startCard.addRow(
            new HelpNavigationLinkView(
                "Ava „Minu teatised“",
                "Näed sulle saadetud teatisi. Lehe avamine üksi ei märgi neid nähtuks.",
                () => this._logRoute("notifications")
            ),
            RHSpacing.small
        )
        this._startCard.addRow(
            new HelpNavigationLinkView(
                "Ava avalik statistika",
                "Vaata riigihangete koondnäitajaid ning hankijate ja pakkujate võrdlusi.",
                () => this._statisticsRoute()
            ),
            RHSpacing.small
        )
    }

    _buildTendererDiscoveryCard() {
        this._tendererDiscoveryCard.addRow(
            this._paragraph(
                "Pakkujana alustad hanke leidmisest. Avalikku hanget saab vaadata ka sisse logimata. " +
                "Mitteavalikku hanget näevad selleks õigustatud ettevõtete kasutajad või kutsekirjas oleva " +
                "isikustatud lingi saajad."
            ),
            RHSpacing.medium
        )
        this._tendererDiscoveryCard.addRow(
            new HelpFlowDiagramView([
                { icon: "search", title: "Leia", detail: "„Hanked“, „Otsing“ või saadud kutse" },
                { icon: "visibility", title: "Kontrolli", detail: "Avalik või mitteavalik hange" },
                { icon: "register", title: "Registreeru", detail: "Ettevõte registreeritakse hankele" },
                { icon: "message", title: "Täpsusta", detail: "Küsi hankijalt" },
                { icon: "edit", title: "Koosta", detail: "Vali osad, sisesta hinnad ja lisa failid" },
                { icon: "send", title: "Esita", detail: "Enne lõplikku tähtaega" }
            ]),
            RHSpacing.large
        )
        this._tendererDiscoveryCard.addRow(this._subheading("Kuidas hanke leiad ja osalema asud?"), RHSpacing.large)
        const discoveryBullets = [
            "Avalik hange on hankeloendis ja otsingus nähtav ka kasutajale, kes pole sisse loginud. " +
            "Pakkumuse esitamiseks peab kasutajal olema õigus tegutseda ettevõtte nimel.",
            "Mitteavalik hange ei ilmu avalikku hankeloendisse. Seda näevad kutsutud või juba hankele " +
            "registreerunud ettevõtete kasutajad. Hanke saab avada ka kutsekirjas oleva isikustatud lingi kaudu.",
            "Ettevõtte kehtiv CPV-valik on automaatsete CPV-põhiste ettevõttesoovituste alus. Valik koosneb " +
            "keskkonna vaikekoodidest ja peakasutaja lisatud CPV-koodidest. Soovitus ei ole kutse.",
            "Kui hanke CPV-kood vastab sinu jaoks kehtivale CPV-valikule, võib uue avaliku hanke " +
            "avaldamisel muutuda menüüvalik „Hanked“ rasvaseks. Peakasutaja teavitused lähtuvad " +
            "ettevõtte CPV-valikust. Pakkuja ettevõtte esindaja hanketeavitustes arvestatakse vaikimisi " +
            "nii ettevõtte CPV-valikut kui ka enda teavituste jaoks lisatud CPV-koode. Kui esindaja " +
            "eemaldab märke valikult „Arvesta ettevõtte CPV-valikut“, arvestatakse ainult enda " +
            "teavituste jaoks lisatud CPV-koode. Need lisakoodid ei piira avalike hangete nähtavust ega " +
            "muuda ettevõtte soovitamist hankijale.",
            "Kutsekiri saadetakse kutse juures määratud e-posti aadressile ka siis, kui selle aadressiga konto on " +
            "juba olemas. Isikustatud hanke juurdepääsulink kehtib vähemalt 7 päeva; kui hanke tähtaeg on kaugemal, " +
            "siis tähtajani, kuid kõige rohkem 90 päeva. Konto puudumisel sisaldab kiri lisaks registreerumislinki, " +
            "mis kehtib hanke tähtajani, kuid samuti kõige rohkem 90 päeva.",
            "Hankele registreerumine seob ettevõtte hankega. Pakkuja meeskond määrab, kes saab " +
            "pakkumusega töötada ja millises rollis."
        ]
        this._addBullets(this._tendererDiscoveryCard, discoveryBullets)
        this._tendererDiscoveryCard.addRow(
            new RHCalloutView(
                "important",
                "Registreerumise eeldused",
                "Hange peab olema avaldatud, pakkumuste esitamise tähtaeg ei tohi olla möödunud ning " +
                "mitteavaliku hanke puhul peab sinu ettevõttel olema hanke vaatamise õigus. Registreeruda saab " +
                "ettevõtte aktiivne peakasutaja või pakkuja ettevõtte esindaja. Hanget korraldav ettevõte ei saa " +
                "oma hankel pakkujana registreeruda. Samal hankel hankemeeskonda kuuluv kasutaja ei saa huvide " +
                "konflikti tõttu pakkujana registreeruda. Kui ettevõte pole veel " +
                "registreeritud, teeb süsteem seda pakkumuse esmakordsel salvestamisel."
            ),
            RHSpacing.large
        )
        this._tendererDiscoveryCard.addRow(
            this._subheading("Täpsustav hange ja järgmised voorud"),
            RHSpacing.large
        )
        const reconciliationBullets = [
            "Hankija võib luua ühe hankeosa kohta täpsustava hanke ja valida sinna selle osa eelmise vooru " +
            "pakkujad. Täpsustav hange on eraldi mitteavalik hange, mis avaldatakse kohe valitud ettevõtetele.",
            "Valitud ettevõte registreeritakse uude vooru automaatselt ning saab kutsekirja ja hankija " +
            "selgitava teate. Uues voorus tuleb koostada ja esitada uus pakkumus uueks tähtajaks; eelmise " +
            "vooru pakkumust üle ei kanta.",
            "Hanke ülaosas saad liikuda 1. vooru ja järgmiste voorude vahel. Näed ainult neid voore, millele " +
            "sinu ettevõttel on juurdepääs."
        ]
        this._addBullets(this._tendererDiscoveryCard, reconciliationBullets)
        this._tendererDiscoveryCard.addRow(
            new HelpNavigationLinkView(
                "Sirvi pakkuja hankeid",
                "Ava vaade „Hanked“, kus näed avalikke, sinu ettevõttele nähtavaid mitteavalikke ja ettevõtte osalusega hankeid.",
                () => this._userMainRoute("offerer")
            ),
            RHSpacing.large
        )
        this._tendererDiscoveryCard.addRow(
            new HelpNavigationLinkView(
                "Ava „Leitavus ja hanketeavitused“",
                "Peakasutaja saab täiendada ettevõtte CPV-valikut, lisades keskkonna vaikekoodidele CPV-koode. " +
                "Pakkuja ettevõtte esindaja näeb ettevõtte CPV-valikut, saab valida, kas seda tema " +
                "hanketeavitustes arvestatakse, ning lisada CPV-koode ainult enda teavituste jaoks.",
                () => this._companyRoute("cpvInterests", "offerer")
            ),
            RHSpacing.small
        )
    }

    _buildTendererOfferCard() {
        this._tendererOfferCard.addRow(
            this._paragraph(
                "Pakkumus koostatakse hankeosade kaupa. Võid pakkuda ühele või mitmele osale. Kui " +
                "hankija on lubanud samale osale mitu pakkumust, saad neid lisada määratud ülempiirini " +
                "või piiramatult."
            ),
            RHSpacing.medium
        )
        this._tendererOfferCard.addRow(this._subheading("Enne esitamist"), RHSpacing.large)
        const preparationBullets = [
            "Kontrolli iga lisatud pakkumuse hinda ja selle juurde lisatud faile.",
            "Laadi iga pakkumuse juurde üles hankeosa kohta nõutud dokumendid. Kui nõutud dokument " +
            "puudub, ei saa pakkumuse kinnitamist jätkata ega pakkumust esitada.",
            "Hankele registreerinud kasutaja määratakse üldjuhul pakkumuse vastutavaks isikuks. Kui " +
            "pakkumust alustab teine kasutaja ja registreerija saab endiselt ettevõtte nimel tegutseda, " +
            "lisatakse alustaja volitatud isikuks.",
            "Vastutav isik ja volitatud isikud saavad mustandit koostada, muuta ning pakkumuse esitada.",
            "Mustandi salvestamisel ega muutmisel hankijale teatist ei saadeta."
        ]
        this._addBullets(this._tendererOfferCard, preparationBullets)
        this._tendererOfferCard.addRow(
            new RHCalloutView(
                "warning",
                "Konfidentsiaalsus enne tähtaega",
                "Kuni pakkumuste avamiseni ei näe hankija esindajad hindu, faile ega muid pakkumuse " +
                "sisulisi andmeid. Pärast lõpliku tähtaja möödumist saab pakkumused avada hankija meeskonnaliige, " +
                "kellel on hindamisõigus."
            ),
            RHSpacing.large
        )
        this._tendererOfferCard.addRow(this._subheading("Esitamine, muutmine ja tagasivõtmine"), RHSpacing.large)
        const submitSteps = [
            "Kontrolli kinnitusekraanil kõik lisatud pakkumused üle ja vajuta „Esita pakkumus“ või " +
            "„Esita pakkumused“.",
            "Pärast edukat esitamist kuvatakse vaates „Hanked“ teisel real „Pakkumus esitatud“ ning " +
            "„Minu pakkumus“ vaates kinnitatakse esitatud hankeosad. Hanke põhirida jääb enne tähtaega " +
            "olekusse „Alustatud“.",
            "Kuni lõpliku tähtajani saab vastutav isik või volitatud isik muuta või tagasi võtta ühe hankeosa kohta esitatud " +
            "pakkumuse. Teised sama hankeosa pakkumused jäävad jõusse.",
            "Kui lubatud ülempiir ja hankeosa olek seda võimaldavad, saab vastutav isik või volitatud isik kuni lõpliku tähtajani " +
            "lisada veel alternatiivi või pakkumuse uuele hankeosale.",
            "Kui võtad tagasi viimase jõus oleva osapakkumuse, loetakse kogu pakkumus tagasivõetuks. Enne " +
            "lõplikku tähtaega saad seejärel koostada ja esitada uue pakkumuse.",
            "Pärast lõplikku tähtaega pakkumust enam esitada, lisada, muuta ega tagasi võtta ei saa."
        ]
        this._addNumberedRows(this._tendererOfferCard, submitSteps)
        this._tendererOfferCard.addRow(
            new RHCalloutView(
                "info",
                "Keda esitamisest teavitatakse?",
                "Pakkumuse esitamisel saavad hankija vastutav isik, volitatud isikud ja hindajad kohe " +
                "rakenduses teatise. „Pakkumused“ muutub lugemata teabe korral rasvaseks alles pärast " +
                "tähtaja möödumist. Hindamist ootavat tööd näitab siis oranž täpp. Esitatud pakkumuse " +
                "hilisem muutmine tekitab samale hankemeeskonnale uue rakendusesisese teatise; e-kirja ei saadeta."
            ),
            RHSpacing.large
        )
        this._tendererOfferCard.addRow(
            new RHCalloutView(
                "info",
                "Täpsustavas hankes koosta uus pakkumus",
                "Eelmise vooru pakkumust täpsustavasse hankesse automaatselt üle ei kanta. Koosta ja esita " +
                "uue vooru pakkumus selle hanke osale ning järgi uut tähtaega."
            ),
            RHSpacing.small
        )
    }

    _buildTendererResultCard() {
        this._tendererResultCard.addRow(
            this._paragraph(
                "Tähtaja möödumine ei tähenda veel tulemust. Hankija avab ja hindab pakkumusi " +
                "hankeosade kaupa. Sobiva pakkumuse korral saab hankija märkida võitja; hankeosa hindamise " +
                "võib lõpetada ka võitjata."
            ),
            RHSpacing.medium
        )
        this._tendererResultCard.addRow(
            new HelpFlowDiagramView([
                { icon: "deadline", title: "Lõplik tähtaeg", detail: "Pakkumuste esitamine lõpeb" },
                { icon: "open", title: "Avamine", detail: "Hankija avab pakkumused" },
                { icon: "evaluate", title: "Hindamine", detail: "Iga osa eraldi" },
                { icon: "winner", title: "Tulemus", detail: "Hankija lõpetab hankeosa hindamise" },
                { icon: "contract", title: "Leping", detail: "Igale võidetud hankeosale luuakse eraldi lepingukirje" }
            ]),
            RHSpacing.large
        )
        const resultBullets = [
            "Pärast lõpliku tähtaja möödumist muutub hanke põhirida olekusse „Hindamisel“, kuid pakkumuse " +
            "sisu jääb peidetuks kuni hankija selle avab. Avamisel saavad pakkumuse vastutav isik ja " +
            "volitatud isikud rakenduses teatise ning e-kirja.",
            "Võitja märkimine võib hanke staatust pakkuja vaates „Hanked“ kohe muuta, kuid see ei kinnita " +
            "veel tulemust ega saada tulemuseteatist. Tulemus kinnitatakse ja teatis saadetakse alles siis, " +
            "kui hankija lõpetab hankeosa hindamise ja teavitab pakkujaid.",
            "Kui esitasid samale hankeosale mitu pakkumust, võid saada iga pakkumuse tulemuse kohta " +
            "eraldi teatise ja e-kirja. Need saadetakse pakkumuse vastutavale isikule ja volitatud isikutele.",
            "Hindamise lõpetamisel luuakse iga võidetud hankeosa kohta eraldi lepingukirje, ka siis, kui sama " +
            "ettevõte võidab mitu osa. Leping ei ole veel sõlmitud: selleks peab hankija salvestama " +
            "allkirjastamise kuupäeva.",
            "Ühe, mitme või kõigi hankeosade sõlmitud lepingute puhul kuvatakse vastavalt „Leping hankeosale " +
            "on sõlmitud“, „Lepingud hankeosadele on sõlmitud“ või „Leping on sõlmitud“.",
            "Pakkumuse vastutav isik ja volitatud isikud saavad oma ettevõtte lepinguid vaadata. " +
            "Ainuüksi ettevõtte hankele registreerimisest selleks ei piisa. Lepinguandmeid saab muuta " +
            "ainult vastava õigusega hankija meeskonnaliige.",
            "Lepingu allkirjastamise kuupäeva esmakordsel salvestamisel või lepingu sõlmimata jätmise " +
            "märkimisel saavad võitnud pakkumuse vastutav isik ja volitatud isikud rakenduses teatise. " +
            "E-kirja ei saadeta. " +
            "Varem sõlmituks märgitud lepingu saab hankija parandada olekusse „Ei sõlminud lepingut“; " +
            "sel juhul eemaldatakse lepingu number ja allkirjastamise kuupäev. Lepingu numbri muutmisel " +
            "ega hilisemal allkirjastamise kuupäeva muutmisel eraldi teatist ei saadeta.",
            "Kui kõik hanke kehtivad sõlmitud lepingud on täidetud ja lõpetatuks märgitud, kuvatakse hange " +
            "olekus „Teostatud“."
        ]
        this._addBullets(this._tendererResultCard, resultBullets)
        this._tendererResultCard.addRow(
            new HelpNavigationLinkView(
                "Ava pakkuja vaade „Hanked“",
                "Leia hange, ava selle üksikasjad ja seejärel vahekaart „Lepingud“.",
                () => this._userMainRoute("offerer")
            ),
            RHSpacing.large
        )
    }

    _populateProcurementCreationHelpCard() {
        this._procurerCreateCard.addRow(
            this._paragraph(
                "Uus hange on koostamise ajal ettevalmistamisel. Nupu „Hanke eelvaade →“ vajutamisel " +
                "kontrollib süsteem kohustuslikke välju, salvestab hanke ja avab eelvaate. Vormil vali " +
                "hanke liigiks „Riigihange“ või „Konkurss“, määra tulevane tähtaeg ning otsusta, kas " +
                "eeldatav kogumaksumus avaldatakse pakkujatele."
            ),
            RHSpacing.medium
        )
        this._procurerCreateCard.addRow(
            new HelpFlowDiagramView([
                { icon: "edit", title: "Ettevalmistamisel", detail: "Nimetus ja tähtaeg" },
                { icon: "document", title: "Hankeosad", detail: "Kirjeldus ja CPV-kood" },
                { icon: "document", title: "Dokumendid", detail: "Hankija failid ja dokumendinõuded" },
                { icon: "team", title: "Meeskond", detail: "Vastutav isik ja õigused" },
                { icon: "preview", title: "Eelvaade", detail: "Kontroll enne avaldamist" }
            ]),
            RHSpacing.large
        )
        this._procurerCreateCard.addRow(this._subheading("Hankeosad ja CPV-koodid"), RHSpacing.large)
        const partBullets = [
            "Hankel peab alati olema vähemalt üks hankeosa, mistõttu viimast osa ei saa eemaldada. Iga " +
            "osa puhul on kohustuslikud kirjeldus ja CPV-kood. Eeldatava maksumuse saab lisada soovi " +
            "korral. Lepingu kestus tuleb määrata igale hankeosale sõltumata valitud CPV-koodist.",
            "Lepingu kestuse saad määrata alusdokumentides, kuudes, päevades või kuupäevavahemikuna. " +
            "Kuupäevavahemiku puhul on lõppkuupäev kohustuslik ja alguskuupäev vabatahtlik.",
            "Hankeosa jaoks vali üks CPV-kood kas otsinguväljalt või CPV-koodide puust. Valitud koodi " +
            "alusel koostatakse kutsutavate ettevõtete soovitus. Soovitust saad täiendada käsitsi või kleepides " +
            "CSV-vormingus read ning otsustad ise, millised ettevõtted tegelikult kutsuda.",
            "Kutsutavate ettevõtete valikus saad eraldi sisse või välja lülitada allikad „Statistika“, " +
            "„Ettevõtte CPV-huvid“ ja „Käsitsi/import“. Võrdlemiseks kuvatakse muu hulgas maksuvõlg, sama " +
            "CPV-koodiga lepingute arv, käive ja töötajate arv.",
            "Kui CPV-kood ja eeldatav maksumus on sisestatud, näitab „CPV-summeerimise kontroll“ viimase 12 kuu " +
            "summeerimist, piirmäärale lähenemist või selle ületamist ning arvestatud kirjeid. See on " +
            "riskimärguanne, mitte automaatne rikkumise otsus ega hanke avaldamise takistus.",
            "Soovi korral luba pakkujal esitada ühe hankeosa kohta mitu pakkumust. Pakkumuste arvu võib " +
            "jätta piiramata või määrata ülempiiri. Pakkuja võib esitada pakkumuse ainult valitud hankeosadele.",
            "Automaatse tähtaja pikenemise kasutamisel peavad nii nõutud pakkumuste arv kui ka pikendamise " +
            "päevade arv olema positiivsed täisarvud."
        ]
        this._addBullets(this._procurerCreateCard, partBullets)
        this._procurerCreateCard.addRow(this._subheading("Hankedokumendid ja dokumendinõuded"), RHSpacing.large)
        const fileBullets = [
            "Hankedokumendid on hankija lisatud failid. Dokumendinõuded määravad, millised failid peab " +
            "pakkuja pakkumusele lisama.",
            "Mitmeosalise hanke puhul märgi iga dokumendinõude juures hankeosad, millele nõue kehtib.",
            "Iga fail võib olla kuni 10 MB. Hankija hankedokumendi vorming võib olla PDF, DOC, DOCX, XLS, " +
            "XLSX, PNG, JPG/JPEG, ASICE, BDOC, DDOC või CSV.",
            "Pakkuja pakkumuse dokumentidele ja dokumendinõuete failidele on lisaks lubatud ODT, TXT ja ZIP.",
            "Kui pakkuja pole hankeosa suhtes kehtiva dokumendinõude täitmiseks faili lisanud, ei saa ta " +
            "selle osa pakkumust esitada."
        ]
        this._addBullets(this._procurerCreateCard, fileBullets)
        this._procurerCreateCard.addRow(
            this._subheading("Alusta varasemast hankest või mallist"),
            RHSpacing.large
        )
        const sourceBullets = [
            "„Kopeeri varasem hange“ kannab uude vormi üle põhiseaded, hankeosad, dokumendinõuded ja " +
            "kutsutavad ettevõtted. Tähtaeg tuleb määrata uuesti ning hankedokumentide faile ei kopeerita.",
            "„Alusta mallist“ täidab vormi valitud malli järgi. Mall võib lukustada CPV-koodid ja hankeosade " +
            "struktuuri, nõutud dokumendid või pakkujate ringi; lukustatud osa ei saa uues hankes muuta.",
            "Aktiivset malli saab kasutada ettevõtte liige, kellel on hanke loomise õigus. Malle haldab " +
            "ettevõtte peakasutaja."
        ]
        this._addBullets(this._procurerCreateCard, sourceBullets)
        this._procurerCreateCard.addRow(
            new RHCalloutView(
                "warning",
                "Salvesta muudatused enne lahkumist",
                "Kui liigud rakenduses mujale ja vormil on salvestamata muudatusi, küsib süsteem enne " +
                "lahkumist kinnitust."
            ),
            RHSpacing.large
        )
        this._procurerCreateCard.addRow(
            new HelpNavigationLinkView(
                "Alusta uut hanget",
                "Lülitab hankija vaatesse ning avab „Töölaua“ all vahekaardi „Alusta uut hanget“.",
                () => this._procurerMainRoute("new")
            ),
            RHSpacing.large
        )
        this._procurerCreateCard.addRow(
            new HelpNavigationLinkView(
                "Ava „Ettevalmistamisel ja mallid“",
                "Jätka varem salvestatud hanke koostamist.",
                () => this._procurerMainRoute("drafts")
            ),
            RHSpacing.small
        )
    }

    _buildProcurerPublishCard() {
        this._procurerPublishCard.addRow(
            this._paragraph(
                "Enne avaldamist vali eelvaate jaotises „Avaldamise viis“, kas tegemist on avaliku või " +
                "mitteavaliku hankega. Valik määrab, kes näeb hanget ja selle dokumente."
            ),
            RHSpacing.medium
        )
        this._procurerPublishCard.addRow(
            new RHCalloutView(
                "info",
                "Avalik hange",
                "Hange kuvatakse avalikus hankeloendis ja otsingutulemustes ning selle andmeid ja " +
                "hankedokumente saab vaadata ka sisse logimata. Avalikku hanget saab vaadata sõltumata " +
                "kasutaja CPV-valikust. " +
                "Kui hanke CPV-kood kattub kasutaja jaoks kehtiva CPV-valikuga, võib menüüvaliku „Hanked“ " +
                "nimi muutuda rasvaseks."
            ),
            RHSpacing.large
        )
        this._procurerPublishCard.addRow(
            new RHCalloutView(
                "warning",
                "Mitteavalik hange",
                "Hange ei ilmu avalikku hankeloendisse. Seda saavad vaadata hankija vastava õigusega " +
                "kasutajad, kutsutud või hankele registreerunud ettevõtete kasutajad ning kutsekirja " +
                "isikustatud lingi saajad. Avaldamiseks tuleb valida vähemalt üks kutsutav ettevõte. " +
                "Hankedokumentide avamiseks peab kasutaja olema sisse logitud ja tal peab olema hanke juurdepääs."
            ),
            RHSpacing.small
        )
        this._procurerPublishCard.addRow(this._subheading("Mis juhtub hanke avaldamisel?"), RHSpacing.large)
        const publishSteps = [
            "Hanke saab avaldada vastutav isik või volitatud isik. Süsteem kontrollib hanke nimetust, tulevast " +
            "tähtaega, vastutavat isikut, hankeosi, osade kirjeldusi, " +
            "CPV-koode ja dokumendinõuete nimetusi. Kui lubatud on mitu pakkumust ühe hankeosa kohta või " +
            "automaatne tähtaja pikenemine, kontrollitakse ka nende seadistust. Lisaks peab olema tehtud " +
            "eeldatava kogumaksumuse avaldamise valik ja määratud iga osa lepingu kestus. Mitteavalikul " +
            "hankel peab olema vähemalt üks kutsutav ettevõte.",
            "Hankele määratakse viitenumber ja avaldamisaeg ning ettevalmistamisel hange muutub avaldatud hankeks.",
            "Pärast avaldamist ilmub hange selle nägemiseks õigustatud kasutajate hankenimekirja. Avaliku " +
            "hanke korral võib sobiva CPV-valikuga kasutaja näha valiku „Hanked“ juures uue hanke märguannet.",
            "Iga kutse juures määratud e-posti aadress saab isikustatud hankelingiga kutsekirja. Kui aadress " +
            "pole seotud kasutajakontoga, lisatakse kirjale ka registreerumislink.",
            "Hanke avaldamise kohta luuakse rakenduse teatis hanke volitatud isikutele.",
            "Hanke jagamisel saab saata lingi korraga kuni 50 e-posti aadressile. Jagamiskiri ei anna " +
            "saajale automaatselt juurdepääsu mitteavalikule hankele ega tekita rakenduses teatist."
        ]
        this._addNumberedRows(this._procurerPublishCard, publishSteps)
        this._procurerPublishCard.addRow(
            new RHCalloutView(
                "important",
                "Kontrolli enne nupu „Avalda hange“ vajutamist",
                "Kas hanke nähtavus vastab eesmärgile? Kas mitteavalikule hankele on kutsutud õiged ettevõtted? " +
                "Kas tähtaeg ja hankeosadele määratud kohustuslikud dokumendid on õiged?"
            ),
            RHSpacing.large
        )
        this._procurerPublishCard.addRow(
            this._subheading("Avaldatud hanke muutmine"),
            RHSpacing.large
        )
        const amendmentBullets = [
            "Vastutav isik või volitatud isik saab valida „Muuda andmeid“, kuni pakkumusi ei ole avatud. " +
            "Muudatuse selgitus on kohustuslik ja enne salvestamist kuvatakse erinevuste eelvaade.",
            "Muudatust kirjeldav automaatteade koos selgitusega lisatakse „Teabevahetusse“. Lihtsa väärtuse " +
            "muutmisel näidatakse vana ja uut väärtust. Rakenduse teatise ja e-kirja saavad asjaomase ettevõtte " +
            "pakkumuse vastutav isik ja volitatud isikud; aktiivse pakkumuse puudumisel osalemise vastutav " +
            "isik ja volitatud isikud. Muule aktiivsele kutseaadressile saadetakse e-kiri.",
            "Muudatusega saab muuta ka avaldamise viisi ja kutsutavate ringi. Eemaldatud kutsete " +
            "isikustatud lingid tühistatakse.",
            "Avaldatud hanke linki saab jagada korraga kuni 50 e-posti aadressile. Jagamiskiri ei anna " +
            "saajale mitteavaliku hanke juurdepääsu."
        ]
        this._addBullets(this._procurerPublishCard, amendmentBullets)
    }

    _buildProcurerEvaluationCard() {
        this._procurerEvaluationCard.addRow(
            this._paragraph(
                "Pärast avaldamist vasta küsimustele, saada vajaduse korral üldteateid ning jälgi tähtaega. " +
                "Pakkumuste sisu saab avada alles pärast lõpliku tähtaja möödumist. Võitja märkimine ja hankeosa " +
                "hindamise lõpetamine on eri toimingud."
            ),
            RHSpacing.medium
        )
        this._procurerEvaluationCard.addRow(
            new HelpFlowDiagramView([
                { icon: "message", title: "Vasta", detail: "Küsimused ja üldteated" },
                { icon: "deadline", title: "Jälgi", detail: "Tähtaeg" },
                { icon: "open", title: "Ava", detail: "Pakkumused pärast tähtaega" },
                { icon: "evaluate", title: "Hinda", detail: "Hankeosad ja pakkumused" },
                { icon: "winner", title: "Vali", detail: "Võitja vajaduse korral" },
                { icon: "contract", title: "Lõpeta", detail: "Teavita pakkujaid" }
            ]),
            RHSpacing.large
        )
        const evaluationSteps = [
            "Vasta hanke kohta esitatud küsimustele ja saada vajaduse korral kõigile registreerunud " +
            "ettevõtetele üldteade.",
            "Kui lõplik tähtaeg möödub, saavad vastutav isik, volitatud isikud ja hindajad " +
            "rakenduses teatise ning e-kirja. Kui mõne pakkumuse esitamise teatis on veel vaatamata, " +
            "kuvatakse vahekaart „Pakkumused“ rasvasena. Hindamist ootavat tööd näitab oranž täpp.",
            "Pärast lõpliku tähtaja möödumist vali „Ava pakkumused ja teavita pakkujaid“. Enne avamist näed " +
            "pakkumuse esitanud ettevõtte nime, esitamise aega ja hankeosi, millele pakkumus esitati, kuid mitte pakkumuse " +
            "sisulisi andmeid ega faile. Avamine on pöördumatu; seda saab teha hindamisõigusega meeskonnaliige " +
            "pärast huvide konflikti puudumise kinnitamist.",
            "Pärast pakkumuste avamist saab hanke üksikasjade juurest alla laadida avamisprotokolli Excel-faili. " +
            "Protokolli näeb hankemeeskonna liige, kellel on hanke üksikasjade vaatamise õigus. Kui pakkumusi " +
            "ei esitatud, protokolli ei koostata.",
            "Märgi, kas pakkumus vastab hanketingimustele, ja lisa vajaduse korral tulemuse selgitus. Seda " +
            "saavad teha vastutav isik, volitatud isikud ja hindajad pärast huvide konflikti puudumise kinnitamist.",
            "Vastutav isik või volitatud isik saab hanketingimustele vastavaks märgitud pakkumuse märkida " +
            "hankeosas edukaks. " +
            "Hindaja pakkumust edukaks märkida ei saa. Hankeosa hindamise saab lõpetada ka ühtegi pakkumust edukaks märkimata.",
            "Vastutav isik lõpetab iga hankeosa hindamise nupuga „Lõpeta osa N hindamine ja teavita " +
            "pakkujaid“; üheosalise hanke puhul on nupul „Lõpeta hindamine ja teavita pakkujaid“. Toiming " +
            "lukustab tulemuse ning loob võitja korral sellele hankeosale eraldi lepinguandmed.",
            "Kui kõik hankeosad on lõpetatud, lõpeb kogu hanke hindamine."
        ]
        this._addNumberedRows(this._procurerEvaluationCard, evaluationSteps)
        this._procurerEvaluationCard.addRow(
            new RHCalloutView(
                "warning",
                "Võitja märkimine ei lõpeta hindamist",
                "Veerus „Võitja“ tehtud valik salvestab hankeosa võitja, kuid ei lõpeta hindamist ega " +
                "teavita veel pakkujaid. Võitja korral luuakse sellele hankeosale eraldi lepinguandmed ning tulemuseteatised " +
                "saadetakse alles siis, kui vastutav isik lõpetab hankeosa hindamise."
            ),
            RHSpacing.large
        )
        this._procurerEvaluationCard.addRow(
            this._subheading("Täpsustav hange"),
            RHSpacing.large
        )
        const reconciliationBullets = [
            "Kui hankeosa hindamine on veel avatud, saab vastutav isik või volitatud isik valida „Loo " +
            "täpsustav hange“. Valida saab vähemalt ühe selle osa tegeliku, tagasivõtmata pakkumuse esitanud ettevõtte.",
            "Määra uus tulevane tähtaeg ja kirjuta kohustuslik selgitus. „Loo ja avalda hange“ loob kohe " +
            "ühe hankeosaga mitteavaliku hanke, kopeerib hankemeeskonna ning registreerib valitud ettevõtted.",
            "Valitud pakkujad saavad kutse ja selgitus avaldatakse uue hanke „Teabevahetuses“. 1. vooru hankeosa " +
            "tulemus lukustatakse ning võitja otsus tehakse täpsustavas hankes.",
            "Täpsustavast hankest saab vajaduse korral luua järgmise vooru. 1. vooru ja järgnevate voorude " +
            "vahel saab liikuda hanke ülaosas.",
            "Vastutav isik saab lukustatud osa puhul valida „Loobu täpsustamisest“. See lõpetab osa võitjata " +
            "ning tulemust hiljem muuta ei saa."
        ]
        this._addBullets(this._procurerEvaluationCard, reconciliationBullets)
        this._procurerEvaluationCard.addRow(
            this._subheading("Ilma lepinguta lõpetamine ja lepingu elutsükkel"),
            RHSpacing.large
        )
        const contractLifecycleBullets = [
            "Vastutav isik saab avaldatud hanke lõpetada ilma lepinguta. Põhjus on kohustuslik ja lisatakse " +
            "„Teabevahetusse“.",
            "Mitmeosalises hankes saab vastutav isik lõpetada ilma lepinguta ka üksiku veel lõpetamata osa. " +
            "Selle osa aktiivsed pakkumused tühistatakse ja pakkujaid teavitatakse; teised osad jätkuvad.",
            "Hindamise lõpetamisel tekib iga võidetud osa kohta ootel leping. Vastutav või volitatud isik " +
            "saab salvestada lepingu numbri ja allkirjastamise kuupäeva või märkida „Ei sõlminud lepingut“. " +
            "Mittesõlmimise põhjus on kohustuslik.",
            "Ka varem sõlmituks märgitud lepingu saab parandada mittesõlmituks; siis eemaldatakse lepingu number " +
            "ja allkirjastamise kuupäev ning mittesõlmimise otsus on lõplik.",
            "Sõlmitud lepingu saab hiljem märkida lõpetatuks. Ootel lepinguga hange on „Lõpetatud“, kehtiva " +
            "sõlmitud lepinguga „Täitmisel“ ning pärast kõigi kehtivate lepingute lõpetamist „Teostatud“."
        ]
        this._addBullets(this._procurerEvaluationCard, contractLifecycleBullets)
        this._procurerEvaluationCard.addRow(
            new HelpNavigationLinkView(
                "Ava „Minu hanked“",
                "Vaata, millised hanked ootavad pakkumuste avamist või hindamise lõpetamist.",
                () => this._procurerMainRoute("published")
            ),
            RHSpacing.large
        )
    }

    _buildTeamCard() {
        this._teamCard.addRow(
            this._paragraph(
                "Ettevõtte roll määrab, milliseid vaateid saad kasutada. Hankemeeskonna või pakkuja meeskonna " +
                "roll määrab sinu õigused konkreetses hankes või pakkumuses."
            ),
            RHSpacing.medium
        )
        this._teamCard.addRow(this._subheading("Ettevõtte rollid"), RHSpacing.large)
        const companyRoleBullets = [
            "Peakasutaja — haldab ettevõtte liikmeid, liikmekutseid, liitumistaotlusi ja rolle. Roll sisaldab " +
            "hankija ja pakkuja ettevõtterolle, kuid Hankija vaade on saadaval ainult ettevõttele, kes saab " +
            "rakenduses hankijana tegutseda.",
            "Hankija ettevõtte esindaja — saab kasutada Hankija vaadet, kui aktiivne ettevõte saab hankijana " +
            "tegutseda, ning toimida talle antud hankemeeskonna õiguste piires.",
            "Pakkuja ettevõtte esindaja — saab kasutada pakkuja vaadet ja tegutseda pakkumustega talle " +
            "antud pakkuja meeskonna õiguste piires.",
            "Audiitor — saab aktiivse ettevõtte avaldatud hangete üksikasju, pärast pakkumuste avamist " +
            "pakkumusi ja lepinguid vaadata, kuid ei näe mustandeid ega saa andmeid muuta. " +
            "Audiitorit ei saa lisada hankemeeskonda ega pakkuja meeskonda.",
            "Ülaribal kuvatakse aktiivse ettevõttega lubatud vaated. Kui sul on mitu lubatud vaadet, saad " +
            "nende vahel samas kohas vahetada."
        ]
        this._addBullets(this._teamCard, companyRoleBullets)
        this._teamCard.addRow(this._subheading("Hankemeeskond"), RHSpacing.large)
        const procurerRoleBullets = [
            "Vastutav isik — igas meeskonnas on üks vastutav isik. Ta haldab hanget, meeskonda ja vastutava " +
            "isiku rolli, vastab küsimustele, hindab pakkumusi, valib võitja ning lõpetab hankeosa hindamise.",
            "Volitatud isik — muudab hanget, haldab meeskonnaliikmeid, vastab küsimustele, hindab pakkumusi ja märgib pakkumuse edukaks. " +
            "Ta ei halda vastutava isiku rolli ning hankeosa hindamise lõpetamine jääb " +
            "vastutavale isikule.",
            "Ettevõtte otsene peakasutaja saab sama ettevõtte kõigi hangete meeskonnaliikmeid hallata, " +
            "vastutavat isikut vahetada ning end meeskonda lisada. Peakasutaja roll üksi ei anna hanke " +
            "sisulist detailvaadet.",
            "Hindaja — näeb hindamiseks vajalikku teavet ja saab hinnanguid salvestada, kuid ei saa võitjat " +
            "valida ega hankeosa hindamist lõpetada.",
            "Vaatleja — saab andmeid vaadata, kuid mitte muuta.",
            "Kõik hankemeeskonna liikmed peavad kinnitama huvide konflikti puudumist. Kuni kinnitamiseni on " +
            "hanke üksikasjalik ligipääs suletud ning vahekaardid „Pakkumused“ ja „Teabevahetus“ ei avane."
        ]
        this._addBullets(this._teamCard, procurerRoleBullets)
        this._teamCard.addRow(this._subheading("Registreeritud hankele"), RHSpacing.large)
        const participationRoleBullets = [
            "Pärast hankele registreerumist ja enne pakkumuse loomist on pakkuja meeskonnas vastutav isik " +
            "ning volitatud isikud. Mõlemad saavad pakkumust koostada ja esitada.",
            "Registreerumise vastutav isik, volitatud isik või ettevõtte peakasutaja saab kolleegidele " +
            "volitatud isiku rolli määrata. Peakasutaja, kes veel meeskonda ei kuulu, saab lisada ennast " +
            "volitatud isikuna.",
            "Meeskonna ajalugu kuvatakse hanke „Pakkuja meeskond“ vahekaardil osalemise vastutavale isikule, " +
            "volitatud isikutele ja ettevõtte peakasutajale.",
            "Pakkumuse loomisel luuakse eraldi meeskond: registreeruja määratakse üldjuhul vastutavaks " +
            "ning muu alustaja ja osalemise volitatud isikud kantakse pakkuja meeskonda volitatud isikutena üle."
        ]
        this._addBullets(this._teamCard, participationRoleBullets)
        this._teamCard.addRow(this._subheading("Pakkuja meeskond"), RHSpacing.large)
        const offererRoleBullets = [
            "Vastutav isik — saab pakkumust koostada ja esitada, volitatud isikuid hallata ning vastutava " +
            "isiku rolli üle anda.",
            "Volitatud isik — saab pakkumust koostada ja esitada ning volitatud isikuid hallata, kuid ei saa " +
            "vastutavat isikut vahetada.",
            "Ettevõtte peakasutaja saab volitatud isikuid hallata ja vastutavat isikut vahetada. Kui ta ei kuulu " +
            "veel pakkuja meeskonda, saab ta pärast huvide konflikti puudumise kinnitamist lisada ennast ainult " +
            "volitatud isikuna.",
            "Meeskonna ajalugu kuvatakse hanke „Pakkuja meeskond“ vahekaardil pakkumuse vastutavale isikule, " +
            "volitatud isikutele ja ettevõtte peakasutajale."
        ]
        this._addBullets(this._teamCard, offererRoleBullets)
        this._teamCard.addRow(
            new RHCalloutView(
                "important",
                "Pakkumusel peab olema vastutav isik",
                "Pakkumusel peab olema üks vastutav isik. Kui meeskonnas pole teist sobivat vastutajat, " +
                "ei saa tema rolli eemaldada. Kui sobiv kasutaja on olemas, saab vastutuse talle üle anda " +
                "ja senise vastutava isiku rolli eemaldada."
            ),
            RHSpacing.large
        )
        this._teamCard.addRow(
            new HelpNavigationLinkView(
                "Ava „Administreerimine“",
                "Peakasutaja saab seal hallata liikmeid ja ettevõtte rolle.",
                () => this._companyRoute("administration")
            ),
            RHSpacing.large
        )
    }

    _buildMessagingCard() {
        this._messagingCard.addRow(
            this._paragraph(
                "Vahekaart „Teabevahetus“ jaguneb alamvaadeteks „Avalik“ ja „Privaatne“. Avalikus vaates " +
                "on hanke küsimused, vastused, hankija üldteated ja automaatteated. Privaatses vaates peab " +
                "hankija iga pakkujaettevõttega eraldi vestlust."
            ),
            RHSpacing.medium
        )
        this._messagingCard.addRow(this._subheading("Avalik teabevahetus"), RHSpacing.large)
        const publicMessagingBullets = [
            "Teabevahetust näevad üksikasjade vaatamise õigusega hankemeeskonna liikmed ning pakkumuse " +
            "vastutav isik ja volitatud isikud. Ainuüksi ettevõtte hankele registreerimisest ei piisa.",
            "Pakkuja meeskond näeb oma ettevõtte küsimuse juures ettevõtte nime; teiste ettevõtete " +
            "küsimuste esitajad jäävad anonüümseks.",
            "Üksikasjade vaatamise õigusega hankija meeskonna liikmed näevad kõiki küsimusi " +
            "ning küsimuse esitaja nime ja ettevõtet.",
            "Pakkumuse vastutav isik ja volitatud isikud saavad küsimusi esitada kogu hanke avaldatud " +
            "etapi jooksul, ka pärast pakkumuste esitamise tähtaega.",
            "Hanke vastutav isik ja volitatud isikud saavad vastata küsimustele ning saata kõigile registreeritud " +
            "pakkujatele üldteate. Avalik küsimuste ja teadete saatmine lõpeb, kui hange ei ole enam avaldatud etapis.",
            "Küsimusele, vastusele ja hankija üldteatele saab lisada kuni 10 MB suuruseid PDF-, DOC-, DOCX-, " +
            "XLS-, XLSX-, PNG- või JPG/JPEG-faile.",
            "Avaldatud hanke muutmisel lisatakse automaatteade, mis kirjeldab muudatust ja sisaldab hankija " +
            "selgitust. Lihtsa väärtuse muutmisel näidatakse vana ja uut väärtust."
        ]
        this._addBullets(this._messagingCard, publicMessagingBullets)
        this._messagingCard.addRow(
            new RHCalloutView(
                "info",
                "Avaliku teabevahetuse teavitused",
                "Uus küsimus saadab hankija vastutavale isikule ja volitatud isikutele rakenduse teatise. " +
                "Vastus või üldteade saadab rakenduse teatise registreeritud pakkujaettevõtete pakkumuse " +
                "vastutavale isikule ja volitatud isikutele; aktiivse pakkumuse puudumisel osalemise vastutavale " +
                "isikule ja volitatud isikutele. Nende sündmuste kohta e-kirja ei saadeta. Avaldatud hanke ajal " +
                "kaob vastamata küsimuse oranž tegevustäpp pärast vastamist; avaldatud etapi lõppemisel " +
                "märki enam ei kuvata."
            ),
            RHSpacing.large
        )
        this._messagingCard.addRow(this._subheading("Privaatne teabevahetus"), RHSpacing.large)
        const privateMessagingBullets = [
            "Iga pakkujaettevõttega on eraldi privaatne vestlus. Hankija näeb kõiki ettevõtte vestlusi; pakkuja " +
            "vastutav isik ja volitatud isikud näevad ainult oma ettevõtte vestlust. Kui hankija saadab sama sõnumi mitmele ettevõttele, luuakse " +
            "igaühega eraldi vestlus ja ettevõtted ei näe teisi saajaid.",
            "Enne pakkumuste esitamise tähtaega saab vestlust alustada hankija vastutav isik või volitatud " +
            "isik registreeritud pakkujaga. Pakkuja saab enne tähtaega vastata ainult hankija alustatud vestluses.",
            "Pärast tähtaega saab privaatset vestlust alustada ainult ettevõttega, kellel on esitatud pakkumus. " +
            "Sellisel juhul saab vestlust alustada ka pakkuja. Privaatne teabevahetus jääb avatuks ka pärast " +
            "hindamise lõpetamist.",
            "Sõnumi tekst on kohustuslik. Lisada saab kuni 10 MB suuruseid PDF-, DOC-, DOCX-, XLS-, XLSX-, " +
            "PNG- või JPG/JPEG-faile.",
            "Uuest privaatsõnumist saab teine pool rakenduse teatise ja e-kirja. E-kiri ei sisalda sõnumi teksti. " +
            "Pakkuja poolel teavitatakse pakkumuse vastutavat isikut ja volitatud isikuid; kui " +
            "aktiivset pakkumust veel ei ole, teavitatakse osalemise vastutavat isikut ja volitatud " +
            "isikuid. Hankija poolel " +
            "teavitatakse vastutavat isikut ja volitatud isikuid.",
            "Lugemata vestlust näitab märge privaatsel alamvaatel ja ettevõtte vestluse real. Lugemata olek " +
            "on sama poole ühine: kui üks õigustatud hankija või pakkuja meeskonnaliige " +
            "vestluse avab, märgitakse vastaspoole sõnumid selle poole jaoks loetuks."
        ]
        this._addBullets(this._messagingCard, privateMessagingBullets)
    }

    _buildDeadlineCard() {
        this._deadlineCard.addRow(
            this._paragraph(
                "Avaldatud hanke tähtaega saab muuta hanke andmete muudatusena. Hanke loomisel saab määrata " +
                "ka automaatse pikenemise, kui pakkumuse esitanud ettevõtteid on nõutud arvust vähem."
            ),
            RHSpacing.medium
        )
        this._deadlineCard.addRow(this._subheading("Tähtaja muutmine käsitsi"), RHSpacing.large)
        const manualDeadlineBullets = [
            "Vastutav isik või volitatud isik valib avaldatud hankel „Muuda andmeid“. Muuta saab seni, kuni " +
            "pakkumusi ei ole avatud, ning uus tähtaeg peab olema tulevikus.",
            "Kui vähemalt üks registreeritud pakkuja ei ole veel pakkumust esitanud, peab uus tähtaeg olema " +
            "vähemalt üks tund tulevikus.",
            "Muudatuse selgitus on kohustuslik. Enne salvestamist kuvatakse muudatuse eelvaade.",
            "Salvestamisel lisatakse „Teabevahetusse“ automaatteade vana ja uue tähtaja ning selgitusega. " +
            "Rakenduse teatise ja e-kirja saavad asjaomase ettevõtte pakkumuse vastutav isik ja volitatud isikud; " +
            "aktiivse pakkumuse puudumisel osalemise vastutav isik ja volitatud isikud. Muule aktiivsele " +
            "kutseaadressile saadetakse e-kiri."
        ]
        this._addBullets(this._deadlineCard, manualDeadlineBullets)
        this._deadlineCard.addRow(this._subheading("Automaatne pikenemine"), RHSpacing.large)
        const automaticDeadlineBullets = [
            "Reeglis määratakse pakkumuse esitanud ettevõtete nõutud arv ja päevade arv, mille võrra tähtaega " +
            "pikendatakse. Süsteem loendab iga esitanud ettevõtet ainult üks kord.",
            "Kui kehtiva tähtaja saabudes on esitanud ettevõtteid nõutust vähem, nihutatakse tähtaega määratud " +
            "päevade võrra. Tähtaeg võib automaatselt pikeneda kuni kolm korda.",
            "Kui nõutud arv ettevõtteid on pakkumuse esitanud või kõik pikendused on kasutatud, tähtaega enam " +
            "ei pikendata ja hange liigub hindamise etappi.",
            "Hankija vastutav isik, volitatud isikud ja hindajad saavad automaatsest pikenemisest rakenduse " +
            "teatise ning e-kirja. Registreeritud pakkuja pakkumuse vastutavale ja volitatud isikutele " +
            "saadetakse rakenduse reaalajauuendus; aktiivse pakkumuse puudumisel kasutatakse osalemise " +
            "vastutavat isikut ja volitatud isikuid. Pakkujale ei saadeta " +
            "rakenduse teatist ega e-kirja. Kui teate saaja on parasjagu selle hanke detailvaates, kuvatakse " +
            "talle ka teatedialoog.",
            "Lõpliku tähtaja möödumisel saavad hankija vastutav isik, volitatud isikud ja hindajad rakenduse " +
            "teatise ning e-kirja.",
            "Avatud vaade võib tähtaja saabudes käivitada kontrolli kohe. Taustakontroll töötab umbes iga viie " +
            "minuti järel, mistõttu võib ilma avatud vaateta uuendus mõne minuti viibida."
        ]
        this._addBullets(this._deadlineCard, automaticDeadlineBullets)
    }

    _buildNotificationsCard() {
        this._notificationsCard.addRow(
            this._paragraph(
                "Tabelis on kasutajale olulised automaatsed teavitused. „Rakenduse teatis“ tähendab " +
                "kirjet lehel „Minu teatised“. Mõni muudatus jõuab avatud vaatesse ainult reaalajas " +
                "ning e-kiri on alati eraldi kanal."
            ),
            RHSpacing.medium
        )
        this._notificationsCard.addRow(
            new RHCalloutView(
                "info",
                "Lugemata info ja tegevust vajav töö on eri asjad",
                "Rasvane kiri tähistab üldjuhul uut või lugemata infot. Töölaual ning vahekaartidel " +
                "„Pakkumused“ ja „Teabevahetus“ tähistab oranž täpp pooleliolevat tegevust, näiteks " +
                "vastamata küsimust või hindamist ootavaid pakkumusi. „Privaatne“ alamvaates on oranž täpp " +
                "erandina lugemata vestluse märk ja kaob, kui vestluse avab mõni sama poole õigustatud kasutaja. " +
                "Teatise avamine märgib selle " +
                "nähtuks; kõik teatised saad korraga puhastada valikuga „Märgi kõik nähtuks“."
            ),
            RHSpacing.large
        )
        const rows: { event: string; recipient: string; channel: string }[] = [
            {
                event: "Mitteavaliku hanke kutse",
                recipient: "Iga hankele lisatud kutseaadress",
                channel: "E-kiri isikliku hankelingiga; kontota adressaadile lisatakse ka registreerumislink"
            },
            {
                event: "Tavalise hanke avaldamine",
                recipient: "Hanke volitatud isikud",
                channel: "Rakenduse teatis; e-kirja ei saadeta"
            },
            {
                event: "CPV-valikule vastava uue avaliku hanke avaldamine",
                recipient: "Ettevõtte aktiivsed peakasutajad ja pakkuja ettevõtte esindajad, kelle kehtiv " +
                    "CPV-valik kattub hanke CPV-koodiga ja kes on rakendust viimase 90 päeva jooksul kasutanud",
                channel: "Pakkuja menüüvalik „Hanked“ on rasvane; e-kirja ei saadeta"
            },
            {
                event: "Uus avalik küsimus",
                recipient: "Hankija vastutav isik ja volitatud isikud",
                channel: "Rakenduse teatis; „Töölaud“ ja „Teabevahetus“ on rasvased ning vastamata " +
                    "küsimust näitab oranž tegevustäpp; e-kirja ei saadeta"
            },
            {
                event: "Avalik vastus või hankija üldteade",
                recipient: "Registreerunud ettevõtete pakkumuse vastutav isik ja volitatud isikud; aktiivse " +
                    "pakkumuse puudumisel osalemise vastutav isik ja volitatud isikud",
                channel: "Rakenduse teatis ja lugemata märk Teabevahetuses; e-kirja ei saadeta"
            },
            {
                event: "Uus privaatne sõnum",
                recipient: "Hankija vastutav ja volitatud isikud või pakkuja vastutav ja volitatud isikud; " +
                    "aktiivse pakkumuse puudumisel osalemise vastutav isik ja volitatud isikud",
                channel: "Rakenduse teatis, e-kiri ilma sõnumi sisuta ja lugemata märk privaatses vestluses"
            },
            {
                event: "Pakkumuse esitamine, esitatud osa muutmine, lisamine või tagasivõtmine",
                recipient: "Hankija vastutav isik, volitatud isikud ja hindajad",
                channel: "Rakenduse teatis; pärast tähtaega kuvatakse hindamist ootavate pakkumuste korral " +
                    "pakkumuste tegevusmärk. E-kirja ei saadeta"
            },
            {
                event: "Avaldatud hanke muutmine, sealhulgas tähtaja käsitsi muutmine",
                recipient: "Asjaomase ettevõtte pakkumuse vastutav isik ja volitatud isikud; aktiivse pakkumuse " +
                    "puudumisel osalemise vastutav isik ja volitatud isikud; lisaks muud " +
                    "aktiivsed kutseaadressid",
                channel: "Kasutajale rakenduse teatis ja e-kiri, muule aktiivsele kutseaadressile e-kiri; " +
                    "registreerunud pakkujale lisatakse Teabevahetusse automaatne avalik muudatusteade"
            },
            {
                event: "Tähtaja automaatne pikenemine",
                recipient: "Hankija vastutav isik, volitatud isikud ja hindajad",
                channel: "Rakenduse teatis ja e-kiri"
            },
            {
                event: "Tähtaja automaatne pikenemine",
                recipient: "Registreerunud pakkuja pakkumuse vastutav isik ja volitatud isikud; aktiivse pakkumuse " +
                    "puudumisel osalemise vastutav isik ja volitatud isikud",
                channel: "Avatud rakendusse jõuab reaalajauuendus; hanke detailvaates kuvatakse teade. " +
                    "Rakenduse teatist ega e-kirja ei saadeta"
            },
            {
                event: "Pakkumuste esitamise tähtaja möödumine",
                recipient: "Hankija vastutav isik, volitatud isikud ja hindajad",
                channel: "Rakenduse teatis ja e-kiri; võimalik on ligikaudu viieminutiline viivitus"
            },
            {
                event: "Pakkumuste avamine",
                recipient: "Iga esitatud pakkumuse vastutav isik ja volitatud isikud",
                channel: "Rakenduse teatis, lugemata märk „Minu pakkumus“ vaates ja e-kiri"
            },
            {
                event: "Hankeosa hindamise lõpetamine",
                recipient: "Sellele osale pakkumuse teinud ettevõtte pakkumuse vastutav isik ja " +
                    "volitatud isikud",
                channel: "Rakenduse teatis, lugemata märk „Minu pakkumus“ vaates ja e-kiri iga " +
                    "pakkumusvariandi tulemuse kohta"
            },
            {
                event: "Täpsustava hanke loomine",
                recipient: "Valitud pakkujad",
                channel: "Kutse e-kiri; automaatse registreerimise järel ka rakenduse teatis hankija " +
                    "selgitava avaliku teate kohta"
            },
            {
                event: "Kogu hanke lõpetamine ilma lepinguta",
                recipient: "Asjaomase ettevõtte pakkumuse vastutav isik ja volitatud isikud; aktiivse pakkumuse " +
                    "puudumisel osalemise vastutav isik ja volitatud isikud; lisaks muud " +
                    "aktiivsed kutseaadressid",
                channel: "Kasutajale rakenduse teatis ja e-kiri, muule aktiivsele kutseaadressile e-kiri; " +
                    "põhjus avaldatakse Teabevahetuses"
            },
            {
                event: "Ühe hankeosa lõpetamine ilma lepinguta",
                recipient: "Selle osa aktiivse pakkumuse vastutav isik ja volitatud isikud",
                channel: "Rakenduse teatis ja e-kiri"
            },
            {
                event: "Hankelingi jagamine",
                recipient: "Hankija valitud adressaadid",
                channel: "Ainult e-kiri"
            },
            {
                event: "Hankemeeskonda lisamine või meeskonnarolli muutmine",
                recipient: "Lisatud kasutaja või kasutaja, kelle roll muutus",
                channel: "Rakenduse teatis; e-kirja ei saadeta"
            },
            {
                event: "Pakkumuse vastutava isiku muutmine",
                recipient: "Uus vastutav isik",
                channel: "Rakenduse teatis; e-kirja ei saadeta"
            },
            {
                event: "Lepingu esmakordne sõlmituks märkimine või esmakordne sõlmimata jäetuks märkimine",
                recipient: "Võitnud pakkumuse vastutav isik ja volitatud isikud",
                channel: "Rakenduse teatis; e-kirja ei saadeta"
            },
            {
                event: "Muude lepinguandmete muutmine või lepingu lõpetatuks märkimine",
                recipient: "Võitnud ettevõte",
                channel: "Teatist ega e-kirja ei saadeta"
            }
        ]
        const notificationMatrix = new HelpMatrixView("Teavituste saajad ja kanalid")
        notificationMatrix.addMatrixRow(new HelpMatrixRowView("SÜNDMUS", "SAAJA", "KANAL", YES, NO))
        for (let index = 0; index < rows.length; index++) {
            let isShaded = NO
            if (index % 2 === 1) {
                isShaded = YES
            }
            const row = rows[index]
            notificationMatrix.addMatrixRow(
                new HelpMatrixRowView(row.event, row.recipient, row.channel, NO, isShaded)
            )
        }
        this._notificationsCard.addRow(notificationMatrix, RHSpacing.large)
        this._notificationsCard.addRow(this._subheading("Lehe „Teavitused“ vahekaardid"), RHSpacing.large)
        const notificationPageBullets = [
            "„Minu teatised“ — sulle saadetud teatised. Lugemata teatis on esile tõstetud. Teatise avamisel " +
            "liigud võimaluse korral seotud hanke juurde ja teatis märgitakse nähtuks.",
            "„Minu tegevused“ — sinu toimingute ajalugu ja üksikasjad.",
            "Lehe avamine ei märgi kõiki teatisi nähtuks. Ava teatis või vali „Märgi kõik nähtuks“."
        ]
        this._addBullets(this._notificationsCard, notificationPageBullets)
        this._notificationsCard.addRow(
            new HelpNavigationLinkView(
                "Ava „Minu teatised“",
                "Vaata teatisi ja liigu neist seotud hanke juurde.",
                () => this._logRoute("notifications")
            ),
            RHSpacing.large
        )
        this._notificationsCard.addRow(
            new HelpNavigationLinkView(
                "Ava „Minu tegevused“",
                "Vaata oma toimingute ajalugu.",
                () => this._logRoute("eventLog")
            ),
            RHSpacing.small
        )
    }

    _buildCompanyCard() {
        this._companyCard.addRow(
            this._paragraph(
                "Vaates „Administreerimine“ näed aktiivset ettevõtet, oma rolle ning ettevõttega seotud " +
                "kutseid ja taotlusi. Peakasutaja haldab seal liikmeid, rolle ja liitumistaotlusi. Kui sul " +
                "on liikmesus mitmes ettevõttes, saad aktiivset ettevõtet vahetada kasutajamenüüst."
            ),
            RHSpacing.medium
        )
        this._companyCard.addRow(this._subheading("Liikmed ja rollid"), RHSpacing.large)
        const roleBullets = [
            "Peakasutaja haldab liikmeid ja rolle, saadab liikmekutseid, kinnitab või lükkab tagasi " +
            "liitumistaotlusi ning sisaldab hankija ja pakkuja ettevõtterolle. Hankija vaade eeldab lisaks, " +
            "et ettevõte saab rakenduses hankijana tegutseda.",
            "Hankija ettevõtte esindaja saab kasutada Hankija vaadet, kui ettevõte saab hankijana tegutseda; " +
            "pakkuja ettevõtte esindaja saab kasutada Pakkuja vaadet. Kui kasutajale on antud mõlemad " +
            "esindaja rollid ja ettevõte vastab hankija eeldusele, kuvatakse talle mõlemad vaated.",
            "Audiitor saab aktiivse ettevõtte avaldatud hankeid ning pärast pakkumuste avamist pakkumusi ja " +
            "lepinguid üksikasjalikult vaadata, kuid ei saa andmeid muuta.",
            "Ettevõttel peab alati alles jääma vähemalt üks peakasutaja."
        ]
        this._addBullets(this._companyCard, roleBullets)
        this._companyCard.addRow(this._subheading("Liikme kutsumine"), RHSpacing.large)
        const inviteSteps = [
            "Ava „Administreerimine“ ja vali „Kutsu uus liige“.",
            "Sisesta kasutaja e-posti aadress, soovi korral nimi, ning vali roll. Peakasutaja on üks valik, mis annab " +
            "kasutajale automaatselt nii hankija kui ka pakkuja ettevõtte rolli. Hankija ja pakkuja ettevõtte " +
            "esindaja rolli saab samale kasutajale anda ka koos.",
            "Kontota adressaadile luuakse konto ning saadetakse ajutine salasõna ja kutse aegumiseni kehtiv " +
            "seadistuslink. Olemasolev kinnitamata konto saab seadistuslingi.",
            "Olemasoleva kinnitatud konto kutse jääb ootele. Kasutaja logib sisse seniste andmetega ning " +
            "võtab kutse ettevõttevaates vastu või lükkab selle tagasi; uut salasõna ei looda.",
            "Kasutaja liikmesus muutub aktiivseks alles pärast kutse vastuvõtmist või konto seadistamise " +
            "lõpetamist."
        ]
        this._addNumberedRows(this._companyCard, inviteSteps)
        this._companyCard.addRow(this._subheading("Ettevõttega liitumine"), RHSpacing.large)
        const joinBullets = [
            "Vali „Seo ennast ettevõttega“, et otsida sobiv ettevõte ja saata liitumistaotlus. Seda saad " +
            "teha ka siis, kui oled juba mõne teise ettevõtte liige; sama ettevõtte aktiivset liikmesust " +
            "uuesti ei looda.",
            "Taotluses vali üks roll: Peakasutaja, Hankija ettevõtte esindaja või Pakkuja ettevõtte " +
            "esindaja. Hankija rolli ei saa valida ettevõttes, kes ei saa rakenduses hankijana tegutseda.",
            "Ettevõtte peakasutaja saab taotluse kohta rakenduse teatise ja e-kirja ning kinnitab või lükkab " +
            "taotluse tagasi vaates „Administreerimine“.",
            "Nii kinnitamisest kui tagasilükkamisest saab taotleja rakenduse teatise ja e-kirja.",
            "Kui ettevõtet süsteemis pole, saad taotleda selle registreerimist. Ettevõte luuakse pärast " +
            "äriregistris olevale ettevõtte ametlikule e-posti aadressile saadetud kinnituslingi avamist; " +
            "kinnitus ei lähe automaatselt taotleja enda aadressile. Pärast kinnitamist seotakse taotleja " +
            "ettevõttega peakasutajana."
        ]
        this._addBullets(this._companyCard, joinBullets)
        this._companyCard.addRow(this._subheading("Ettevõttest lahkumine"), RHSpacing.large)
        this._companyCard.addRow(
            this._paragraph(
                "Aktiivsest ettevõttest saad lahkuda ettevõttevaates. Kui oled ettevõtte ainus peakasutaja, " +
                "pead enne lahkumist määrama teise peakasutaja."
            ),
            RHSpacing.small
        )
        this._companyCard.addRow(this._subheading("Hankija liik"), RHSpacing.large)
        this._companyCard.addRow(
            this._paragraph(
                "Ettevõtte otsene peakasutaja saab „Administreerimise“ jaotises „Hankeseaded“ valida hankija " +
                "liigiks „Klassikaline hankija“ või „Võrgustikusektori hankija“. Valik määrab, milliseid " +
                "piirmäärasid kasutatakse hanke CPV-koodide summeerimise aruandes."
            ),
            RHSpacing.small
        )
        this._companyCard.addRow(this._subheading("Pakkuja CPV-valikud"), RHSpacing.large)
        this._companyCard.addRow(
            new RHCalloutView(
                "info",
                "Ettevõtte CPV-valik",
                "Leht „Ettevõtte leitavus ja hanketeavitused“ on saadaval Pakkuja vaates. Ettevõtte CPV-valik " +
                "koosneb keskkonna vaikekoodidest ja peakasutaja lisatud CPV-koodidest. " +
                "Keskkonna vaikekoodide uuendamisel uueneb automaatselt ka ettevõtte valiku vaikeosa. Nii " +
                "vaike- kui ka lisakoodid mõjutavad ettevõtte peakasutajate hanketeavitusi ning määravad, " +
                "milliste hangete puhul kuvatakse ettevõtet hankijale osalema kutsumise soovitustes."
            ),
            RHSpacing.large
        )
        const cpvSelectionBullets = [
            "Peakasutaja valiku „Kasuta ainult keskkonna vaikekoode“ sisselülitamine eemaldab ettevõtte " +
            "lisatud CPV-koodid. Keskkonna vaikekoodid jäävad ettevõtte CPV-valiku aluseks.",
            "Ettevõtte CPV-valiku päises olev „Kustuta lisatud koodid“ eemaldab ainult peakasutaja lisatud " +
            "CPV-koodid. Keskkonna vaikekoodid jäävad valikusse.",
            "Pakkuja ettevõtte esindaja näeb ettevõtte kehtivat CPV-valikut, kuid ei saa seda muuta. Ta saab " +
            "lisada CPV-koode ainult enda hanketeavituste jaoks.",
            "Esindaja valik „Arvesta ettevõtte CPV-valikut“ määrab, kas tema hanketeavitustes arvestatakse " +
            "lisaks enda lisatud CPV-koodidele ka ettevõtte CPV-valikut. Märke eemaldamisel arvestatakse " +
            "ainult tema enda lisatud koode.",
            "Esindaja täiendavate CPV-koodide päises olev „Kustuta kõik“ eemaldab ainult tema lisakoodid ega " +
            "muuda valiku „Arvesta ettevõtte CPV-valikut“ olekut."
        ]
        this._addBullets(this._companyCard, cpvSelectionBullets)
        this._companyCard.addRow(
            new HelpNavigationLinkView(
                "Ava „Administreerimine“",
                "Vaata oma seost ettevõttega — ettevõtte nime ja rolli. Peakasutaja saab seal " +
                "lisaks hallata liikmeid, rolle ja liitumistaotlusi.",
                () => this._companyRoute("administration")
            ),
            RHSpacing.large
        )
        this._companyCard.addRow(
            new HelpNavigationLinkView(
                "Ava „Leitavus ja hanketeavitused“",
                "Avab pakkuja vaates lehe „Ettevõtte leitavus ja hanketeavitused“. Peakasutaja saab seal " +
                "täiendada ettevõtte CPV-valikut, lisades keskkonna vaikekoodidele CPV-koode. Pakkuja ettevõtte " +
                "esindaja näeb ettevõtte CPV-valikut ning saab lisada CPV-koode ainult enda teavituste jaoks.",
                () => this._companyRoute("cpvInterests", "offerer")
            ),
            RHSpacing.small
        )
    }

    _buildStatusCard() {
        this._statusCard.addRow(
            this._paragraph(
                "Hanke staatus kuvatakse tavaliselt kahel real. Rasvane põhirida näitab hanke üldist " +
                "elutsükli etappi. Selle all olev teine rida täpsustab sinu tegevust, ettevõtte osalemist " +
                "või tulemust. Seetõttu võib sama põhistaatus eri kasutajatele näidata erinevat lisateavet."
            ),
            RHSpacing.medium
        )
        this._statusCard.addRow(this._subheading("Hanke elutsükli põhirida"), RHSpacing.large)
        const lifecycleStatuses = [
            "„Ettevalmistamisel“ — hange on mustandina koostamisel ja pole avaldatud.",
            "„Alustatud“ — hange on avaldatud ning pakkumuste esitamise tähtaeg pole möödunud.",
            "„Hindamisel“ — lõplik tähtaeg on möödunud ja hanke tulemust pole veel lõpetatud.",
            "„Lõpetatud“ — hindamine on lõpetatud või hange on lõpetatud ilma lepinguta; täpsem põhjus võib " +
                "olla kuvatud teisel real.",
            "„Täitmisel“ — vähemalt üks kehtiv leping on sõlmitud, kuid kõik sellised lepingud pole veel " +
                "lõpetatuks märgitud.",
            "„Teostatud“ — kõik kehtivad sõlmitud lepingud on lõpetatuks märgitud.",
            "„Tühistatud“ — ettevalmistamisel hange on tühistatud."
        ]
        this._addBullets(this._statusCard, lifecycleStatuses)
        this._statusCard.addRow(this._subheading("Pakkuja vaate teine rida"), RHSpacing.large)
        const offererStatuses = [
            "Enne tulemust kuvatakse tähtaja loendur, „Hankele registreeritud“, „Pakkumus pooleli“ või " +
                "„Pakkumus esitatud“ vastavalt sellele, kui kaugele ettevõte on jõudnud.",
            "„Märgitud edukaks“, „Märgitud hankeosas edukaks“ või „Märgitud hankeosades edukaks“ — sinu " +
                "pakkumus on märgitud edukaks ühes või mitmes osas. Lõplik tulemuseteatis saadetakse osa " +
                "hindamise lõpetamisel.",
            "„Sinu pakkumust ei tunnistatud edukaks“ või „Ühtegi pakkumust ei tunnistatud edukaks“ — sinu " +
                "ettevõtte pakkumus ei võitnud või hange lõpetati võitjata.",
            "„Hankeosa hindamine lõpetatud; sinu pakkumust ei tunnistatud selles osas edukaks.“ või " +
                "„Hankeosade hindamine lõpetatud; sinu pakkumust ei tunnistatud üheski osas edukaks.“ — " +
                "mitmeosalise hanke tulemus on osade kaupa lõpetatud.",
            "„Leping hankeosale on sõlmitud“, „Lepingud hankeosadele on sõlmitud“ või „Leping on sõlmitud“ — " +
                "allkirjastamise kuupäev on salvestatud vastavalt ühele, mitmele või kõigile võidetud osadele.",
            "„Lepingut ei sõlmitud“ — võitnud pakkumuse leping märgiti sõlmimata jäetuks."
        ]
        this._addBullets(this._statusCard, offererStatuses)
        this._statusCard.addRow(this._subheading("Hankija vaate teine rida"), RHSpacing.large)
        const procurerStatuses = [
            "Tähtaja loendur — pakkumuste esitamise aeg veel kestab.",
            "„Vaata pakkumusi“ — pärast tähtaega on esitatud pakkumusi, mida saab avada või hinnata.",
            "„Olete valinud võitja“ — hindamine on lõpetatud, võitja on valitud ja leping ootab otsust.",
            "„Lepingu ootel (x/y)“ — mitmeosalise hanke võitnud osade lepingutest x ootab veel otsust ja " +
                "y on võitnud osade lepingute koguarv.",
            "„Lepingut ei sõlmitud“ — hange lõpetati ilma lepinguta või ükski võitja leping ei jäänud " +
                "kehtivalt sõlmituks."
        ]
        this._addBullets(this._statusCard, procurerStatuses)
        this._statusCard.addRow(this._subheading("Lugemata teave ja märgid"), RHSpacing.large)
        const badgeBullets = [
            "Rasvane pealkiri või nupunimi tähendab üldjuhul, et selles kohas on uut või lugemata teavet. " +
            "See muutub tavaliseks, kui vastav sündmus või sisu märgitakse nähtuks.",
            "Töölaual ning hanke vahekaartidel „Pakkumused“ ja „Teabevahetus“ näitab oranž täpp " +
            "pooleliolevat tegevust, näiteks vastamata küsimust või hindamist ootavaid pakkumusi. " +
            "Täpp kaob pärast vajaliku toimingu tegemist või vastava etapi lõppemisel.",
            "Teabevahetuse alamvaates „Privaatne“ on oranž täpp erandina lugemata sõnumi märk. See kaob, " +
            "kui vastava ettevõtte vestluse avab mõni sama poole õigustatud kasutaja.",
            "„Teavitused“ on rasvane, kui rakenduses on vähemalt üks lugemata teatis.",
            "Rasvane kiri ja oranž täpp võivad olla samal nupul korraga: sisu vaatamine eemaldab lugemata " +
            "märgi, kuid tegevustäpp jääb vajaliku toimingu tegemiseni."
        ]
        this._addBullets(this._statusCard, badgeBullets)
    }

    _buildFaqCard() {
        const questionsAndAnswers: { question: string; answer: string }[] = [
            {
                question: "Kas hankija näeb mu pakkumuse sisu enne tähtaega?",
                answer: "Ei. Hinnad, failid ja muu pakkumuse sisu jäävad peidetuks ka pärast tähtaja " +
                    "möödumist, kuni hankemeeskonna hindamisõigusega liige pakkumused eraldi avab. Enne " +
                    "avamist näeb hankija ainult pakkumuse ümbriku põhiandmeid ja ettevõtte osalemise kokkuvõtet."
            },
            {
                question: "Kuidas kontrollida, kas pakkumus on esitatud?",
                answer: "Vaates „Hanked“ on hanke põhirida enne tähtaega „Alustatud“ ja pärast tähtaega " +
                    "„Hindamisel“; teisel real kuvatakse „Pakkumus esitatud“. „Minu pakkumus“ vaates näitab " +
                    "iga esitatud osa kinnitusriba „Oled sellele osale pakkumuse esitanud“. „Hindamisel“ " +
                    "algab tähtaja möödumisel, mitte pakkumuste avamise nupu vajutamisel."
            },
            {
                question: "Miks ma ei saa pakkumust esitada?",
                answer: "Pakkumuse saab esitada selle vastutav isik või volitatud isik. Kontrolli, et hange on avaldatud, " +
                    "lõplik tähtaeg pole möödunud, vähemalt ühel veel avatud hankeosal on pakkumus nulli või " +
                    "suurema hinnaga, kõik nõutud dokumendid on lisatud ning sul pole blokeerivat huvide konflikti."
            },
            {
                question: "Miks ma ei leia mitteavalikku hanget otsingust?",
                answer: "Mitteavalikku hanget avalikus loendis ei kuvata. Hange on nähtav kutsutud või juba " +
                    "registreerunud ettevõtte kasutajale ning selle saab avada ka kutsekirjas oleva kehtiva " +
                    "isikustatud lingiga. Hankel tegutsemiseks pead olema seotud õige ettevõttega ja omama " +
                    "vajalikku rolli."
            },
            {
                question: "Kust leian pooleliolevad tööd?",
                answer: "Kontrolli esmalt kasutajamenüüst aktiivset ettevõtet. Hankijana leiad avaldatud ja " +
                    "ettevalmistamisel hanked „Töölaualt“, pakkujana ava „Hanked“ ja kasuta staatusefiltreid. " +
                    "Töölaual, mustandites, teatistes ja tegevustes saad vajaduse korral valida „Kõik minu " +
                    "ettevõtted“. Rasvane menüünimi annab märku uuest või lugemata infost."
            },
            {
                question: "Miks võib oranž täpp pärast teatise vaatamist alles olla?",
                answer: "Töölaual ning vahekaartidel „Pakkumused“ ja „Teabevahetus“ näitab oranž täpp " +
                    "tegemata tegevust, mitte lugemata teatist. Avaldatud hanke vastamata küsimuse täpp kaob " +
                    "pärast vastamist või avaldatud etapi lõppemisel. Erand on „Teabevahetus“ → „Privaatne“, kus oranž täpp tähendab " +
                    "lugemata vestlust ja kaob, kui vestluse avab mõni sama poole õigustatud kasutaja."
            },
            {
                question: "Kuidas saada abi vea või puuduva õiguse korral?",
                answer: "Kontrolli aktiivset ettevõtet, valitud Hankija/Pakkuja/Audiitori vaadet, ettevõtterolli, " +
                    "hankemeeskonna või pakkuja meeskonna rolli ning hankemeeskonna huvide konflikti kinnitust. Kui " +
                    "probleem püsib, kirjelda tagasisidevormil samme, oodatud tulemust ja täpset veateadet ning " +
                    "lisa vajaduse korral fail või ekraanipilt. Vorm lisab saatmisel ka lehe konteksti."
            }
        ]
        let gap: number = RHSpacing.medium
        for (const item of questionsAndAnswers) {
            const block = new HelpStackView()
            block.addRow(this._questionLabel(item.question), 0)
            block.addRow(this._paragraph(item.answer), RHSpacing.tiny)
            this._faqCard.addRow(block, gap)
            gap = RHSpacing.large
        }
        this._faqCard.addRow(
            new HelpNavigationLinkView(
                "Anna tagasisidet",
                "Saada tagasisidevormiga probleemi kirjeldus, ettepanek või ekraanipilt.",
                () => this._feedbackRoute()
            ),
            RHSpacing.large
        )
    }


    // MARK: - Repeated content helpers

    _addBullets(card: HelpCardView, bullets: string[]) {
        for (const bullet of bullets) {
            card.addRow(new HelpBulletRowView(bullet), RHSpacing.small)
        }
    }

    _addNumberedRows(card: HelpCardView, steps: string[]) {
        let gap: number = RHSpacing.large
        for (let index = 0; index < steps.length; index++) {
            card.addRow(new HelpNumberedRowView(index + 1, steps[index]), gap)
            gap = RHSpacing.small
        }
    }

    // MARK: - Small label factories

    _eyebrow(text: string): UITextView {
        return new UITextView().configuredWithObject({
            text: text,
            textColor: RHColors.textMuted,
            isSingleLine: NO,
            style: {
                fontFamily: RHFont.familyBase,
                fontSize: RHFont.sizeXSmall,
                fontWeight: RHFont.weightBold,
                lineHeight: RHFont.lineHeightBase,
                letterSpacing: "0.08em"
            }
        })
    }

    _subheading(text: string): UITextView {
        return new UITextView(undefined, UITextView.type.header4).configuredWithObject({
            text: text,
            textColor: RHColors.primaryBlue,
            isSingleLine: NO,
            style: {
                fontFamily: RHFont.familyBase,
                fontSize: RHFont.sizeLarge,
                fontWeight: RHFont.weightSemibold,
                lineHeight: RHFont.lineHeightHeading
            }
        })
    }

    _paragraph(text: string): UITextView {
        return new UITextView().configuredWithObject({
            text: text,
            textColor: RHColors.textPrimary,
            isSingleLine: NO,
            style: {
                fontFamily: RHFont.familyBase,
                fontSize: RHFont.sizeMedium,
                fontWeight: RHFont.weightRegular,
                lineHeight: RHFont.lineHeightBase
            }
        })
    }

    _questionLabel(text: string): UITextView {
        return new UITextView().configuredWithObject({
            text: text,
            textColor: RHColors.textHeading,
            isSingleLine: NO,
            style: {
                fontFamily: RHFont.familyBase,
                fontSize: RHFont.sizeMedium,
                fontWeight: RHFont.weightSemibold,
                lineHeight: RHFont.lineHeightBase
            }
        })
    }


    // MARK: - Navigation helpers

    _routeForHelpSection(sectionIdentifier: string): UIRoute {
        return UIRoute.currentRoute.routeBySettingParameterInComponent(
            HelpViewController.routeComponentName,
            HelpViewController.ParameterIdentifierName.section,
            sectionIdentifier
        )
    }

    _baseRoute(applicationMode?: HelpApplicationMode): UIRoute {
        let route = UIRoute.currentRoute
        if (applicationMode) {
            route = route.routeBySettingParameterInComponent("settings", "mode", applicationMode)
        }
        return route.routeByRemovingComponentsOtherThanOnesNamed(["settings"])
    }

    _userMainRoute(applicationMode: HelpApplicationMode): UIRoute {
        return this._baseRoute(applicationMode).routeWithViewControllerComponent(
            UserMainViewController,
            nil
        )
    }

    _procurerMainRoute(tab: "published" | "drafts" | "new"): UIRoute {
        return this._baseRoute("procurer").routeWithViewControllerComponent(
            UserMainViewController,
            { [UserMainViewController.ParameterIdentifierName.tab]: tab }
        )
    }

    _searchRoute(): UIRoute {
        return this._baseRoute().routeWithViewControllerComponent(
            ProcurementSearchViewController,
            nil
        )
    }

    _logRoute(tab: "notifications" | "eventLog"): UIRoute {
        return this._baseRoute().routeWithViewControllerComponent(
            LogViewController,
            { [LogViewController.ParameterIdentifierName.tab]: tab }
        )
    }

    _companyRoute(tab: "affiliation" | "administration" | "cpvInterests", mode?: HelpApplicationMode): UIRoute {
        return this._baseRoute(mode).routeWithViewControllerComponent(
            CompanyViewController,
            { [CompanyViewController.ParameterIdentifierName.tab]: tab }
        )
    }

    _feedbackRoute(): UIRoute {
        return this._baseRoute().routeWithViewControllerComponent(FeedbackViewController, nil)
    }

    _statisticsRoute(): UIRoute {
        return this._baseRoute().routeWithViewControllerComponent(StatisticsMainViewController, nil)
    }

    _goBack() {
        if (window.history.length > 1) {
            window.history.back()
            return
        }
        this._baseRoute().routeWithViewControllerComponent(UserMainViewController, nil).apply()
    }

    _scrollToCard(card: HelpCardView) {
        let scrollBehavior: ScrollBehavior = "smooth"
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            scrollBehavior = "auto"
        }
        card.viewHTMLElement.scrollIntoView({ behavior: scrollBehavior, block: "start" })
        card._headingLabel.viewHTMLElement.focus({ preventScroll: YES })
    }


    // MARK: - Layout

    override layoutViewSubviews() {
        super.layoutViewSubviews()
        const outerBounds = this.view
            .contentBoundsWithInset(RHSpacing.large)
            .rectangleWithMaxWidth(1000, 0.5)

        let row = outerBounds.rectangleWithHeight(RHButtons.compactHeight)
        this.backButton.frame = row.rectangleWithWidth(this._backButtonWidth())
        row = row.rectangleForNextRow(RHSpacing.large, this.titleLabel)
        this.titleLabel.frame = row
        row = row.rectangleForNextRow(RHSpacing.small, this.introLabel)
        this.introLabel.frame = row
        for (const card of this._cards.filter(card => this.view.hasSubview(card))) {
            row = row.rectangleForNextRow(RHSpacing.large, card)
            card.frame = row
        }
    }

    _backButtonWidth(): number {
        return Math.max(
            this.backButton.titleLabel.intrinsicContentWidth(RHButtons.compactHeight) +
            RHButtons.compactContentPadding * 2 +
            RHSpacing.large,
            RHSpacing.xxLarge * 2 + RHSpacing.large
        )
    }

}
